/**
 * English translations. Keep keys 1:1 with zh.ts.
 */
export default {
  app: {
    name: 'SilkReels',
    tagline: 'AI Comic Drama · Create · Compete · Copyright',
  },
  tenant: {
    suspended: {
      title: 'Service paused',
      tenantLabel: 'Tenant “{{name}}”',
      description:
        'This tenant has been suspended by the platform. Please contact your administrator to restore access.',
      contactTitle: 'Contact',
      phone: 'Support phone',
      email: 'Support email',
      qrcode: 'Support QR code',
      noContact: 'No contact details configured yet. Please reach the platform operator through other channels.',
    },
  },
  nav: {
    home: 'Home',
    works: 'Works',
    contests: 'Contests',
    marketplace: 'Marketplace',
    tasks: 'Tasks',
    learning: 'Learning',
    subscription: 'Membership',
    studio: 'Studio',
  },
  studio: {
    title: 'Creation studio',
    pageSubtitle: 'Collaborative production · writing · creative canvas',
    loginRequired: 'Sign in to open Studio',
    loginRequiredHint: 'Studio requires a signed-in account.',
    featureDisabled: 'Creation studio is not enabled for this tenant',
    migrationPending: 'This module is being migrated into the user app; full features arrive in a follow-up PR.',
    sessionExpired: 'Your session expired. Sign in again to continue.',
    reenterFromPortal: 'Sign in again',
    backHome: 'Back to home',
  },
  modeHub: {
    title: 'Creation studio',
    subtitle: 'Pick a mode: team production, solo writing, or free creative canvas',
    back: 'Back to mode hub',
    enter: 'Open',
    productionTitle: 'Production projects',
    productionSubtitle: 'Team workflow · script, shots, reviews, and delivery',
    writerTitle: 'Writer workspace',
    writerSubtitle: 'Solo writing · no collaboration or assignments',
    creativeTitle: 'Free creative',
    creativeSubtitle: 'Infinite canvas · list on marketplace',
  },
  pagination: {
    prev: 'Previous',
    next: 'Next',
  },
  assignTask: {
    title: 'Assign work',
    hint: 'Pick an assignee and allocate quota. Unassigned scopes are read-only for collaborators.',
    department: 'Budget source (dept sub-pool)',
    departmentPool: 'Production pool',
    departmentPoolHint: 'Leave empty to draw from the production pool; pick a department to draw from its sub-pool.',
    assignee: 'Assignee',
    pickMember: 'Select member',
    assigneeRequired: 'Select an assignee',
    scopeItems: 'Scope items',
    itemsRequired: 'Select at least one bible or shot item',
    quota: 'Assignment quota (points)',
    quotaInvalid: 'Enter a valid quota',
    availableBalance: 'Available {{n}}',
    confirm: 'Confirm assignment',
    cancel: 'Cancel',
    failed: 'Failed to assign',
  },
  budget: {
    panelTitle: 'Production budget',
    panelHint: 'Owner funds the production pool from personal points; optional department sub-pools.',
    empty: 'No budget funded yet',
    loadFailed: 'Failed to load budget',
    poolBalance: 'Pool balance',
    fundedTotal: 'Total funded {{n}}',
    fund: 'Fund',
    fundTitle: 'Fund production pool',
    fundHint: 'Transfer points from your personal balance into this production pool.',
    fundConfirm: 'Confirm fund',
    fundFailed: 'Fund failed',
    allocateDept: 'Allocate to dept',
    allocateTitle: 'Allocate department sub-pool',
    allocateHint: 'Move points from the production pool to a department sub-pool.',
    allocateConfirm: 'Confirm allocate',
    allocateFailed: 'Allocate failed',
    reclaim: 'Reclaim',
    reclaimPrompt: 'Reclaim to production pool (max {{max}})',
    reclaimFailed: 'Reclaim failed',
    deptPools: 'Department sub-pools',
    amount: 'Amount (points)',
    amountInvalid: 'Enter a valid amount',
    note: 'Note',
    dept: 'Department',
    deptRequired: 'Select a department',
    pickDept: 'Pick department',
    insufficientForAI: 'Studio AI draws from the production budget pool. As owner: fund it under Team → Production budget (no assignment needed); collaborators need assignment quota from the owner',
    availableBalance: 'Pool available {{n}}',
    deptCol: 'Department',
    deptBalanceCol: 'Sub-pool balance',
    gateMissingTitle: 'Set a production budget',
    gateMissingBody:
      'This production has no funded budget yet. Studio AI, assignments and reviews draw from the production pool — fund it before you start.',
    gateLowTitle: 'Production budget is low',
    gateLowBody:
      'Remaining balance is {{balance}}, below the warning threshold of {{threshold}}. Fund the pool to avoid interrupting production.',
    gateLater: 'Later',
    gateGoBudget: 'Open budget',
    gateContactOwner: 'Ask the production owner to fund the budget pool.',
  },
  hub: {
    title: 'Productions',
    subtitle: 'Manage script, shots, review and delivery in collaborative productions',
    emptyTitle: 'No productions yet',
    empty: 'Create your first production — solo or invite your team when ready',
    createFirst: 'Create first production',
    create: 'New production',
    count: '{{n}} productions',
    defaultTitle: 'Untitled {{n}}',
    open: 'Open',
    delete: 'Delete',
    edit: 'Edit',
    editTitle: 'Edit production',
    editSaved: 'Production updated',
    editFailed: 'Failed to save',
    deleteConfirm: 'Delete this production? Script, shots and takes will be removed.',
    loading: 'Loading…',
    loadFailed: 'Failed to load productions',
    createFailed: 'Could not create the project. The service is temporarily unavailable — please try again later.',
    deleteFailed: 'Failed to delete production',
    updatedAt: 'Updated {{time}}',
    filterCreated: 'Created by me',
    filterJoined: 'Joined',
    filterAll: 'All',
    episodeTarget: '{{n}} eps',
    myRole: 'My role · {{role}}',
    memberCount: '{{n}} members',
    colTitle: 'Title',
    colType: 'Type',
    colAspect: 'Aspect',
    colEpisodes: 'Episodes',
    colRole: 'My role',
    colMembers: 'Members',
    colUpdated: 'Updated',
    colActions: 'Actions',
  },
  production: {
    loadFailed: 'Production not found or access denied',
    noDescription: 'No description',
    navGroup: {
      pre: 'Pre-production',
      production: 'Production',
      assets: 'Assets',
      post: 'Post',
    },
    nav: {
      backToList: 'Back to productions',
      overview: 'Overview',
      team: 'Team',
      script: 'Script',
      bible: 'Art bible',
      storyboard: 'Storyboard',
      shots: 'Shot list',
      canvas: 'Canvas',
      reviews: 'Review',
      tasks: 'Tasks',
      assets: 'Asset library',
      timeline: 'Timeline',
      settings: 'Settings',
    },
    projectType: {
      short_drama: 'Short drama',
      comic_drama: 'AI comic drama',
      commercial: 'Commercial',
      other: 'Other',
    },
    stats: { aspect: 'Aspect', episodes: 'Episodes', role: 'My role' },
    activityTitle: 'Recent activity',
    activityEmpty: 'No activity yet',
    roles: {
      director: 'Director',
      writer: 'Writer',
      producer: 'Producer',
      executive_producer: 'Executive producer',
      art_director: 'Art director',
      storyboard: 'Storyboard',
      editor: 'Editor',
      contributor: 'Contributor',
    },
  },
  createModal: {
    title: 'New production',
    sectionBasic: 'Basics',
    sectionStorage: 'Asset storage',
    sectionRole: 'My role',
    fieldTitle: 'Title',
    fieldTitlePlaceholder: 'e.g. Season 1 · Project name',
    titleRequired: 'Title is required',
    fieldDescription: 'Description',
    fieldType: 'Type',
    fieldAspect: 'Aspect ratio',
    fieldEpisodes: 'Episode target',
    fieldEpisodesPerSeason: 'Episodes per season',
    fieldEpisodesPerSeasonHint: '0 = no season split; set 12, 24, etc. for multiple seasons',
    fieldLocale: 'Default locale',
    fieldCreatorRole: 'My role in this project',
    storageTenant: 'Tenant cloud storage',
    storageTenantHint: 'Recommended · uses your storage quota',
    storageCustom: 'Custom object storage',
    storageCustomHint: 'Use the OSS / COS profile from Account → Storage',
    storageCustomReady: 'Will use: {{provider}} · {{bucket}} · {{region}}',
    storageCustomMissing: 'No custom object storage is configured yet. Set it up in Account, then come back to create the project.',
    storageCustomGoConfig: 'Configure in Account',
    storageLocalNote: 'Local storage is for self-hosted only',
    submit: 'Create project',
    submitting: 'Creating…',
  },
  overview: {
    todosTitle: 'My todos',
    todosEmpty: 'Nothing pending — start from Tasks or Reviews',
    todoTasks: 'Check task board assignments',
    todoReviews: 'Approve shots in review queue',
    todoComments: 'Reply to shot comments',
    progressTitle: 'Progress',
    progressEmpty: 'No shots yet — add shots first',
    episodeTarget: '{{n}} episodes target',
    memberCount: '{{n}} members',
    quickLinks: 'Quick links',
    shotCount: 'Shots',
    episodeApproved: '{{ep}} · approved {{approved}}/{{total}}',
    storyboarded: 'Storyboard {{n}}/{{total}}',
    withTake: 'Take {{n}}/{{total}}',
  },
  team: {
    title: 'Team',
    subtitle: 'Invite collaborators · project departments',
    seats: 'Seats {{used}} / {{total}}',
    seatsFull: 'Collaboration seats are full. Upgrade or remove a member before inviting.',
    personalMode: 'Solo mode',
    invite: 'Invite member',
    departmentsTitle: 'Departments',
    treeTitle: 'Organization',
    treeEmpty: 'No members yet. Create a department or invite collaborators.',
    treeEmptyWithCollab: 'Add a department or invite directly. Your collaboration plan is enough.',
    deptCreateTitle: 'New department',
    deptEditTitle: 'Edit department',
    deptDescriptionLabel: 'Description (optional)',
    deptDescriptionPlaceholder: 'Brief role of this department',
    editDept: 'Edit',
    inviteToDept: 'Invite here',
    deptMembersEmpty: 'No members in this department',
    deptDeleteBlocked: 'Remove all members before deleting',
    departmentsEmpty: 'No departments yet; solo projects can skip this step.',
    departmentsEmptyWithCollab: 'Add a department to create groups. Your collaboration plan is enough.',
    deptNameLabel: 'Department name',
    deptNamePlaceholder: 'e.g. Writing',
    deptNameRequired: 'Enter a department name first',
    deptCreate: 'Add department',
    deptMemberCount: '{{count}} members',
    deptCreateFailed: 'Failed to create department',
    deptDeleteConfirm: 'Delete this department? (must be empty)',
    deptDeleteFailed: 'Failed to delete department',
    membersTitle: 'Members',
    membersEmpty: 'No members',
    deptAdmin: 'Dept admin',
    remove: 'Remove',
    removeConfirm: 'Remove this member?',
    removeFailed: 'Failed to remove member',
    loadFailed: 'Failed to load team',
    costSummaryHint: 'Cost summary (read-only)',
    costSummaryReadonly: 'Grant from asset library · read-only totals here',
    costSummaryEmpty: 'No cost grants yet',
    costSummaryTotal: 'Total {{total}} points',
    costByDept: 'By department',
    costByMember: 'By member',
    costByAsset: 'By asset',
    makeDeptAdmin: 'Make dept admin',
    revokeDeptAdmin: 'Revoke dept admin',
    patchMemberFailed: 'Failed to update member',
    inviteTitle: 'Invite member',
    fieldRole: 'Production role',
    inviteTabSearch: 'Search users',
    inviteTabEmail: 'Email',
    inviteTabLink: 'Link',
    inviteSearchPlaceholder: 'Username or nickname (min 2 chars)',
    invitePickUser: 'Select a user to invite',
    inviteEmailRequired: 'Email required',
    inviteNoResults: 'No matching users',
    inviteEmailHint: 'When SMTP is configured, an email is sent; you can still copy the link below.',
    inviteEmailSent: 'Invitation email sent (keep the link as backup)',
    inviteEmailNotSent: 'Email not sent — copy the link manually',
    inviteLinkHint: 'One-time link, valid 7 days by default; accepting uses a collaboration seat.',
    inviteSendEmail: 'Create email invite',
    inviteGenerateLink: 'Generate invite link',
    inviteLinkReady: 'Invite link ready — copy and share',
    copyLink: 'Copy',
    searching: 'Searching…',
    fieldDepartment: 'Department',
    noDepartment: 'No department',
    inviteEmailPlaceholder: 'name@example.com',
    inviteConfirm: 'Invite',
    inviting: 'Inviting…',
    inviteFailed: 'Invite failed',
    collabRequired: 'Studio collaboration subscription required to invite',
    upgradeCollab: 'Get collaboration plan',
    seatsExceeded: 'Collaboration seats full — upgrade or remove members',
    ownerOnly: 'Only the project owner can do this',
    memberNotFound: 'Member not found (cannot remove project owner)',
    deptNotFound: 'Department not found',
    deptDeleteTitle: 'Delete department',
    removeTitle: 'Remove member',
    editMember: 'Edit',
    editMemberTitle: 'Edit member',
    noDepartmentGroup: 'Unassigned',
    deptPatchFailed: 'Failed to update department',
    pendingInvitesTitle: 'Pending invites',
    inviteLinkOnly: 'Link invite',
    revokeInvite: 'Revoke',
    revokeInviteTitle: 'Revoke invite',
    revokeInviteConfirm: 'Revoke this invite? The link will stop working immediately.',
    inviteRevokeFailed: 'Failed to revoke invite',
    assignWork: 'Assign work',
    tabMembers: 'Members & org',
    tabBudget: 'Production budget',
    tabAssignments: 'Assignments',
    assignmentsPanelTitle: 'Production assignments',
    assignmentsPanelHint: 'Each assignment binds an assignee, quota, and specific bible/shot items.',
    assignmentsEmpty: 'No assignments yet',
    assignmentsEmptyHint: 'Open Art Bible or Shot List and assign from there.',
    assignmentsNoScope: '(no scope items)',
    assignmentsOpenKanban: 'Open task board',
    assignmentsLoadFailed: 'Failed to load assignments',
    memberQuotaBadge: '{{n}} assignment quota',
    memberScopeCount: '{{count}} items',
    assignmentsColAssignee: 'Assignee',
    assignmentsColDept: 'Department',
    assignmentsColScope: 'Scope',
    assignmentsColQuota: 'Quota',
    assignmentsColStatus: 'Status',
    assignmentsTaskCount: '{{count}} assignments',
    assignmentKindShot: 'Shot',
    assignmentKindScene: 'Scene',
    assignmentSceneTitle: 'Ep {{ep}} · Scene {{scene}}',
    colDepartment: 'Department',
    colMember: 'Member',
    colRole: 'Role',
    colQuota: 'Assignment quota',
    colMemberCount: 'Members',
    colActions: 'Actions',
  },
  bulkAssign: {
    title: 'Bulk assign',
    open: 'Bulk assign',
    tabShots: 'Shots / scenes',
    selectAllTab: 'Select all in tab',
    selectEpisode: 'Select whole episode',
    wholeEpisode: 'Episode {{ep}}',
    episodeGroup: 'By episode',
    emptyTab: 'No assignable items',
    loadFailed: 'Failed to load items',
    confirm: 'Assign ({{count}} items)',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Metadata · storage · danger zone',
    sectionBasic: 'Basics',
    sectionStorage: 'Storage',
    sectionDanger: 'Danger zone',
    storageReadonly: 'Read-only',
    storageAssets: 'Assets',
    storageUsed: 'Used space',
    storageLoadFailed: 'Failed to load storage info',
    storageSave: 'Save storage config',
    storageSaved: 'Storage config updated',
    storageSaveFailed: 'Failed to save storage config',
    customStorageHint: 'Only custom mode credentials can be updated; leave secret blank to keep existing.',
    accessKeySecret: 'Access Key Secret',
    accessKeySecretPlaceholder: 'Leave blank to keep unchanged',
    migrateToTenantOss: 'Migrate to tenant cloud storage',
    migrateConfirm:
      'Migrate from {{from}} to {{to}}? {{assets}} assets (~{{bytes}}), {{copiable}} local files can be copied.',
    migrateDone: 'Migration done: copied {{migrated}}, skipped {{skipped}}.',
    migrateFailed: 'Storage migration failed',
    storageMode: {
      tenant_oss: 'Tenant cloud',
      custom: 'Custom OSS',
      local: 'Local',
    },
    save: 'Save',
    saved: 'Saved',
    saveFailed: 'Save failed',
    loadFailed: 'Failed to load settings',
    deleteHint: 'Deletes script, shots, assets and collaboration data.',
    delete: 'Delete production',
  },
  storageConfig: {
    provider: 'Cloud provider',
    providerAliyun: 'Aliyun OSS',
    providerTencent: 'Tencent COS',
    bucket: 'Bucket',
    region: 'Region',
    endpoint: 'Endpoint',
    accessKeyId: 'AccessKey ID',
    accessKeySecret: 'AccessKey Secret',
    secretId: 'SecretId',
    secretKey: 'SecretKey',
    publicBaseUrl: 'Public URL',
    publicBaseUrlPlaceholder: 'https://cdn.example.com (optional)',
    publicBaseUrlHint: 'CDN or custom domain; leave blank to derive from bucket + endpoint.',
    prefix: 'Object prefix',
    prefixPlaceholder: 'Optional; defaults to project ID subdirectory',
    secretKeepPlaceholder: 'Leave blank to keep unchanged',
    bucketRequired: 'Bucket is required',
    regionRequired: 'Region is required',
    endpointRequired: 'Endpoint is required',
    accessKeyIdRequired: 'AccessKey ID / SecretId is required',
    accessKeySecretRequired: 'AccessKey Secret / SecretKey is required',
  },
  script: {
    title: 'Script',
    subtitle: 'Manage multiple scripts · open the editor to write',
    scriptListSubtitle: '{{count}} scripts · Edit for metadata · Write for body editor',
    unnamedScript: 'Untitled script {{n}}',
    colScriptName: 'Script name',
    colScriptType: 'Type',
    colScriptLocale: 'Language',
    colEpisodes: 'Episodes',
    colSummary: 'Summary',
    addScript: 'New script',
    addScriptTitle: 'New script',
    addScriptHint: 'Add an independent script to this project; structure episodes inside the editor.',
    addScriptConfirm: 'Create',
    addScriptFailed: 'Failed to create script',
    metaCreateHint: 'Set name, cover, and summary; write the script body via Write.',
    metaEditHint: 'Manage script metadata (name, cover, summary), separate from the Fountain editor.',
    metaEditTitle: 'Edit script info',
    metaSave: 'Save info',
    metaSaveFailed: 'Failed to save script info',
    metaSaveAndWrite: 'Save & write',
    fieldSummary: 'Summary',
    fieldSummaryPlaceholder: 'Logline, genre, tone, audience — not the script body',
    fieldCover: 'Cover',
    coverUpload: 'Upload cover',
    coverFromAssets: 'From asset library',
    editMeta: 'Edit',
    writeScript: 'Write',
    fieldScriptTitle: 'Script name',
    fieldScriptTitlePlaceholder: 'Optional, e.g. Season 1 draft',
    fieldScriptType: 'Script type',
    fieldScriptLocale: 'Language',
    localeZhCN: '简体中文',
    localeEnUS: 'English',
    editScript: 'Edit',
    deleteScript: 'Delete',
    deleteScriptTitle: 'Delete script',
    deleteScriptConfirm: 'Delete “{{name}}”? Scenes, revisions, and comments will be removed.',
    deleteScriptFailed: 'Failed to delete script',
    scriptsEmptyTitle: 'No scripts yet',
    scriptsEmpty: 'Create your first script to open the professional editor',
    listSubtitleSingle: '{{target}} episodes · click a row to open the script editor',
    listSubtitleMulti: '{{target}} episodes · {{perSeason}} per season · click a row to open the editor',
    tabWorkspace: 'Workspace',
    tabProduction: 'Production view',
    workspaceSubtitle: 'Series structure · {{target}} episodes · plan top-down, polish per episode',
    treeLabel: 'Structure',
    treeSeries: 'Series outline',
    treeStory: 'Story',
    editorStoryContext: 'Story',
    storyContentHint: 'Write the full story: characters, plot, world. Episode scripts live under Season / Episode / Scene.',
    storyContentPlaceholder: 'e.g.\n【Opening】…\n【Rising action】…\n【Climax】…\n【Resolution】…',
    saveStoryContent: 'Save story',
    seasonDivider: 'Season {{season}}',
    seriesOutlineHint: 'Theme, characters, and the series spine only — no seasons, episodes, or scenes. Write those under each episode.',
    seriesOutlinePlaceholder: 'e.g.\n【Logline】one-sentence story\n【Theme】…\n【Characters】who wants what, at what cost\n【Spine】where it starts, where it turns\n【Undercurrent / world】…',
    treeEpisodeSection: 'Episodes',
    saveSeriesOutline: 'Save series outline',
    seasonViewHint: 'Episode beats and script progress for this season.',
    episodeOutlineHint: 'Beat sheet for this episode; use polish mode for Fountain body.',
    editOutline: 'Edit beats',
    enterPolish: 'Polish mode',
    hasOutline: 'Has beats',
    hasScript: 'Has script',
    aiScopeTitle: 'AI scope',
    aiScopeHint: 'Bulk AI and multi-episode import coming next; scene-level AI remains in polish mode.',
    aiScopeSeries: 'Series: refine theme, characters, and spine',
    aiScopeSeason: 'Season: batch episode beats',
    aiScopeEpisodes: 'Multi-ep: draft from beats',
    aiScopeScene: 'Scene: expand / tighten / polish',
    aiBatchScopeHint: 'Run batch AI for the selected tree node; scene polish stays in polish mode.',
    aiBatchSeriesOutline: 'AI refine series outline',
    aiBatchSeasonOutlines: 'AI season beat sheets',
    aiBatchSeasonScripts: 'AI draft season scripts',
    aiBatchSeasonScriptsHint: 'Draft episodes with beats and no script body yet; whole season in one run.',
    aiBatchEpisodeScript: 'AI draft {{ep}} from beats',
    aiBatchPromptPlaceholder: 'Optional instruction (default if empty)',
    aiBatchFailed: 'Batch AI failed',
    aiBatchNoTargets: 'No eligible episodes for this batch task.',
    aiBatchScopeRequired: 'Select a season or episode scope for batch AI.',
    aiInsufficientPoints: 'Insufficient points for this AI task.',
    aiBatchDone: 'Applied {{count}} items',
    aiImportDone: 'Imported {{count}} episode scripts (draft #{{draftId}})',
    aiModelLabel: 'Text model',
    aiModelPlaceholder: 'Select model',
    aiModelLoading: 'Loading models…',
    aiModelEmpty: 'No text models enabled. Configure pricing in tenant AI center.',
    aiModelCostHint: 'About {{cost}} points per run',
    aiModelCostUnit: '{{cost}} pts',
    aiModelProbe: 'Test model (who are you)',
    aiModelProbeFailed: 'Model probe failed',
    aiModelProbeOk: 'OK · {{ms}} ms · {{cost}} pts',
    aiModelProbeCatalog: 'Requested model: {{name}}',
    aiModelProbeReported: 'Self-reported name: {{name}}',
    aiApplyPreviewCost: 'Preview ({{cost}} pts)',
    aiSuggestCost: 'Suggest ({{cost}} pts)',
    workspaceMeta: '{{target}} episodes · {{perSeason}} per season',
    importTitle: 'Import script',
    importHint: 'Paste or upload Fountain / FDX text; use === EPISODE N === between Fountain episodes.',
    importPlaceholder: '=== EPISODE 1 ===\nINT. ROOM - DAY\n\n...\n\n=== EPISODE 2 ===\n...',
    importMarkerHint: 'Fountain: === EPISODE 1 === (case-insensitive). FDX single file defaults to E01.',
    importChooseFile: 'Choose file',
    importExtendTarget: 'Extend episode_target when import exceeds current target',
    importPreview: 'Preview split',
    importConfirm: 'Write {{count}} episodes',
    importEmpty: 'Paste or upload script content first',
    importReadFailed: 'Failed to read file',
    importPreviewFailed: 'Preview failed',
    importApplyFailed: 'Import failed',
    importParsed: '{{count}} episodes detected',
    importTargetExtend: 'episode_target {{from}} → {{to}}',
    importSelectAll: 'Select all importable',
    importPreviewCol: 'Preview',
    importOverwrite: 'Will overwrite',
    hubSubtitle: '{{target}} episodes · {{perSeason}} per season · open fullscreen editor per episode',
    hubSubtitleSingle: '{{target}} episodes · open fullscreen editor per episode',
    hubSubtitleMulti: '{{target}} episodes · {{perSeason}} per season · open fullscreen editor per episode',
    backToList: 'Back to list',
    editorSeriesContext: 'Series outline · {{target}} episodes',
    episodeTitlePlaceholder: 'Episode name (optional, e.g. Pilot, Turning point)',
    episodeBreadcrumb: '{{season}} · global {{episode}} (ep. {{global}})',
    seasonTitle: 'Season {{season}}',
    seasonTab: 'S{{season}} · {{count}} eps',
    seasonEpisodeCount: '{{count}} episodes',
    colSeasonEpisode: 'Season ep.',
    colGlobalEpisode: 'Global #',
    colStatus: 'Status',
    colScenes: 'Scenes',
    colUpdated: 'Updated',
    colActions: 'Actions',
    editEpisode: 'Edit script',
    deleteEpisode: 'Delete',
    deleteEpisodeTitle: 'Delete episode',
    deleteEpisodeConfirm: 'Delete {{label}}? Scenes, revisions and comments will be removed; later episodes will be renumbered.',
    deleteEpisodeFailed: 'Failed to delete episode',
    deleteEpisodeMin: 'At least one episode must remain',
    addEpisode: 'Add episode',
    addEpisodeTitle: 'New script episode',
    addEpisodeHint: 'Adds {{label}} at the end; edit beats and body in the script editor.',
    addEpisodeConfirm: 'Create',
    addEpisodeFailed: 'Failed to add episode',
    addSeason: 'Add season',
    addSeasonFailed: 'Failed to add season',
    episodesGroup: 'All episodes',
    sceneUntitled: 'Untitled scene',
    addScene: 'Add scene',
    deleteScene: 'Delete scene',
    deleteElement: 'Delete block',
    deleteElementHint: 'Delete current block (Backspace/Delete when empty)',
    aiGenerateTitle: 'Generate all',
    aiCreateTitle: 'AI assist',
    aiCreateHint: 'Enter your request for a single AI response. Copy the result into the script editor on the left and edit as needed.',
    aiCreatePlaceholder: 'e.g. Write a night interior scene where the lead finds a key clue…',
    aiCreateBtn: 'Generate',
    aiResultTitle: 'Result',
    aiResultClear: 'Clear display',
    aiResultEmpty: 'Generate results appear here — copy into the script editor',
    aiResultHint: 'Copy and paste into the script editor on the left.',
    aiHistory: 'History',
    aiHistorySearch: 'Search history…',
    aiHistoryEmpty: 'No matching records',
    aiHistoryClear: 'Clear all',
    aiHistoryClearTitle: 'Clear AI history',
    aiHistoryClearConfirm: 'Delete all generate records for this project. This cannot be undone.',
    aiHistoryGenerate: 'Generate',
    aiHistoryProbe: 'Probe',
    aiHistoryKindGenerate: 'Generate',
    aiHistoryKindProbe: 'Probe',
    aiCopyResult: 'Copy all',
    aiCopied: 'Copied',
    aiCopyFailed: 'Copy failed — select the text and copy manually',
    aiGenerateHint: 'Enter a creative brief; the server builds a standard import document, stores it, then auto-imports episodes (outline/beat sheets still save directly). ≤6 episodes per AI batch; draft overwrites allowed; finalized skipped.',
    aiGeneratePlaceholder: 'e.g. Urban mystery mini-series, forensic lead, one case per episode…',
    aiGenerateAll: 'Outline + all episode scripts',
    aiBatchWarningsTitle: 'Warnings',
    aiBatchWarningsMore: '{{count}} more warnings (see Network response)',
    aiBatchSkipTitle: 'Skipped items',
    aiBatchSkip: {
      finalized: 'Finalized',
      has_body: 'Already has body (overwrite off)',
      no_outline: 'No outline',
      parse_miss: 'Could not parse AI output',
      beyond_episode_target: 'Beyond episode target',
    },
    aiRefineTitle: 'Refine selection',
    aiRefineHint: 'Expand, tighten, or polish the episode or scene selected in the tree.',
    loadEpisodesFailed: 'Failed to load episode list',
    episodesEmptyTitle: 'No script episodes yet',
    episodesEmpty: 'Add the first episode to open the fullscreen professional editor',
    statusEmpty: 'Not started',
    save: 'Save',
    saveWorkingCopy: 'Save working copy',
    finalize: 'Finalize',
    parse: 'Parse scenes',
    statusDraft: 'Draft',
    statusFinalized: 'Rev {{rev}} · finalized',
    finalizedBanner: 'Finalized: edits are working copy only; Rev snapshots are immutable (publish revision in Batch 6)',
    modeWrite: 'Write',
    modeSource: 'Fountain source',
    conventionCn: 'CN Standard',
    conventionHollywood: 'Hollywood Standard',
    formatSceneHeadingCn: '内景 客厅 日',
    formatSceneHeadingHollywood: 'INT. LOCATION - DAY',
    placeholderCn: '内景 客厅 日\n\n...',
    placeholderHollywood: 'INT. CAFE - DAY\n\n...',
    formatStandardHint: 'Save normalizes headings/characters/transitions; PDF export uses Final Draft (Hollywood) or A4 CN layout',
    sceneNav: 'Scene nav',
    writeHint: 'Tab cycles element type · Enter adds next block · syncs with Fountain source mode',
    editorLabel: 'Script body',
    placeholder: 'INT. CAFE - DAY\n\n...',
    scenesLabel: 'Scenes ({{n}})',
    scenesEmpty: 'Add scene headings to auto-parse.',
    elementType: 'Element type',
    dragBlock: 'Drag to reorder',
    undoRedoHint: 'Undo / Redo',
    sceneNo: 'Scene {{n}}',
    loadFailed: 'Failed to load script',
    invalidScript: 'Invalid script',
    episodeLoading: 'Loading episode…',
    saveFailed: 'Failed to save',
    parseFailed: 'Failed to parse',
    finalizeFailed: 'Failed to finalize',
    publishRevision: 'Publish revision',
    publishFailed: 'Failed to publish revision',
    lockRevision: 'Lock this revision',
    lockFailed: 'Failed to lock revision',
    lockedRev: 'Locked Rev {{rev}}',
    tabAi: 'AI',
    tabNotes: 'Notes',
    tabVersions: 'Versions',
    aiDraftHint: 'Draft mode: AI writes directly to the script (points charged)',
    aiFinalizedHint: 'Finalized mode: AI suggests only — adopt, save, then publish revision',
    aiPromptPlaceholder: 'e.g. polish dialogue / tighten scene 2 action…',
    aiApply: 'Apply to script',
    aiSuggest: 'Generate suggestion',
    aiFailed: 'AI assist failed',
    aiCurrentElement: 'Current element',
    aiEmptyElement: 'This element is empty — describe what to write in the prompt.',
    aiQuickPolish: 'Polish current element',
    aiApplied: 'AI changes applied to script',
    aiAdopted: 'Suggestion adopted into working copy',
    aiSuggestionTitle: 'AI suggestion',
    aiUndone: 'AI change reverted',
    aiPreviewTitle: 'AI preview',
    aiPreviewBefore: 'Before',
    aiPreviewAfter: 'After',
    aiPreviewEmpty: '(empty)',
    aiPreviewConfirm: 'Apply changes',
    aiPreviewCancel: 'Cancel',
    aiUndo: 'Undo AI edit',
    aiCurrentScene: 'Scene {{n}}',
    aiSceneExpand: 'Expand scene',
    aiSceneCompress: 'Tighten scene',
    aiSceneNext: 'Generate next scene',
    tabOutline: 'Outline',
    outlineHint: 'Beat sheet for this episode. The agent uses this with the series outline and previous episode context.',
    episodeOutlineLabel: 'Episode beats',
    outlinePlaceholder: 'e.g.\n1. Hook: anonymous letter\n2. Conflict: teammate hides truth\n3. Twist: letter from future self\n4. Cliffhanger: knock at door',
    saveOutline: 'Save outline',
    prevEpisode: 'Previous episode',
    nextEpisode: 'Next episode',
    autoSaving: 'Auto-saving…',
    unsaved: 'Unsaved',
    saved: 'Saved',
    autoSaveFailed: 'Auto-save failed',
    aiCost: 'Charged {{cost}} points',
    adoptSuggestion: 'Adopt to working copy',
    commentPlaceholder: 'Collaboration note…',
    addComment: 'Add note',
    commentFailed: 'Failed to add note',
    commentSelectionHint: '{{n}} chars selected · note will anchor to this passage',
    commentsEmpty: 'No notes yet',
    commentsLoading: 'Loading notes…',
    openCommentsCount: '{{n}} open notes',
    loadMoreComments: 'Load more notes',
    jumpToAnchor: 'Jump to passage',
    resolveComment: 'Resolve',
    resolved: 'Resolved',
    noRevisions: 'No revisions yet',
    exportFountain: 'Export Fountain',
    exportPdf: 'Export PDF',
    exportFdx: 'Export FDX',
    exportFailed: 'Export failed',
    breakdown: 'Generate shots',
    breakdownFailed: 'Breakdown to shots failed',
    breakdownDone: 'Created {{created}} shot drafts from locked script (skipped {{skipped}} existing scenes)',
    localeDefault: 'default',
    listingTitle: 'Script listing (chain C)',
    listingHint: 'Locked E{{ep}} Rev {{rev}} · Creates a marketplace draft, then opens mycanvas sell tab. Revenue goes to project owner.',
    listingName: 'Listing title',
    listingPrice: 'Price (points)',
    listingSummary: 'Listing summary',
    listingConfirm: 'Create draft & open sell',
    listingCancel: 'Cancel',
    listingPriceInvalid: 'Enter a valid price',
    listingFailed: 'Failed to create listing',
    listingStatus: 'Listing {{status}}',
    listScript: 'List for sale',
  },
  shots: {
    title: 'Shot list',
    subtitle: 'Atomic unit for generation and review',
    subtitleV2: 'Structured shots · frames tab · generate takes in workbench',
    subtitleV3: 'Browse episodes by script and season · open storyboard workbench per episode',
    mergeHint: 'List tab for metadata; Frames tab for keyframes. Open shot workbench when visuals are missing.',
    tabList: 'Shot list',
    tabFrames: 'Frames',
    framesEmptyTitle: 'No frames yet',
    framesEmpty: 'Add shots, then generate takes in the workbench',
    add: 'Add shot',
    readOnlyScope: 'Read-only (not assigned)',
    addFirst: 'Add first shot',
    emptyTitle: 'No shots',
    empty: 'Organize AI generation from the shot list',
    workbenchEmpty: 'This shot list is empty — use AI breakdown (right panel) for this scene, or Generate shots in the script editor after locking',
    draftHint: 'Use AI breakdown (right panel) or lock script and run Generate shots in the script editor.',
    openWorkbench: 'Workbench',
    openEpisodeWorkbench: 'Storyboard workbench',
    exportCsv: 'Export CSV',
    loadFailed: 'Failed to load shots',
    createFailed: 'Failed to create shot',
    saveFailed: 'Failed to save shot',
    deleteFailed: 'Failed to delete shot',
    applyFailed: 'Failed to apply shot drafts',
    uploadFailed: 'Failed to upload storyboard image',
    attachFailed: 'Failed to attach asset',
    assetLoadFailed: 'Failed to load asset library',
    backList: 'Back to shot list',
    createModalTitle: 'New shot',
    fieldScript: 'Script',
    fieldEpisode: 'Episode',
    fieldScene: 'Scene',
    fieldStoryboardNote: 'Storyboard note',
    descriptionPlaceholder: 'Shot description and action…',
    dialoguePlaceholder: 'Character lines, VO…',
    soundNotePlaceholder: 'Ambience, foley, SFX…',
    musicNotePlaceholder: 'Score, mood, tempo…',
    storyboardNotePlaceholder: 'Framing, transition, shoot notes…',
    seasonTab: 'Season {{n}}',
    col: {
      index: '#',
      slug: 'Shot',
      scene: 'Sc',
      sceneHeading: 'Scene',
      shotSize: 'Size',
      cameraAngle: 'Angle',
      cameraMovement: 'Move',
      frame: 'Frame',
      description: 'Visual',
      dialogue: 'Dialogue',
      soundNote: 'SFX',
      musicNote: 'Music',
      storyboardNote: 'Notes',
      duration: 'Dur.',
      status: 'Status',
      action: 'Ops',
      episode: 'Episode',
      episodeTitle: 'Title',
      sceneCount: 'Scenes',
      shotCount: 'Shots',
      progress: 'Progress',
    },
    progressApproved: '{{approved}}/{{total}} approved',
    progressEmpty: 'No shots yet',
    noScriptTitle: 'Create a script first',
    noScript: 'Shots are organized by script episodes — create a script document first',
    goScript: 'Go to scripts',
    noEpisodeTitle: 'No episodes',
    noEpisode: 'Write or import episode content in the script editor first',
    goScriptEditor: 'Open script editor',
    episodeWorkbenchTitle: '{{label}} · Storyboard workbench',
    episodeWorkbenchSubtitle: 'Professional shot list · AI breakdown · storyboard strip',
    sceneNav: 'Scenes',
    expandScenePanel: 'Expand scene list',
    collapseScenePanel: 'Collapse scene list',
    expandAiPanel: 'Expand AI breakdown',
    collapseAiPanel: 'Collapse AI breakdown',
    allScenes: 'All scenes',
    noScenes: 'No parsed scenes yet — write or parse scenes in the script editor, then use AI breakdown',
    openCanvasGenerate: 'Open canvas',
    selectAllVisible: 'Select all visible shots',
    selectShot: 'Select shot {{slug}}',
    canvasSelectRequired: 'Select at least one shot first',
    canvasSelectedCount: ' ({{n}})',
    assignWork: 'Assign work',
    assignEpisodeNoScenes: 'No parsed scenes in this episode — cannot assign yet',
    assignScene: 'Assign scene',
    assignShot: 'Assign shot',
    submitSceneReview: 'Submit scene for review',
    sceneReviewNothing: 'No shots in this scene are ready for review (need frames and draft/in-progress status)',
    sceneStoryboardBoard: 'Scene board',
    sceneStoryboardEmpty: 'Upload scene board',
    sceneStoryboardUploadFailed: 'Failed to upload scene board',
    sceneStoryboardPreview: '{{scene}} · scene board',
    episodeSceneListTitle: '{{label}} · scenes',
    episodeSceneListHint: 'Upload or pick reference images per scene from the asset library. The first image becomes the scene board cover.',
    expandEpisodeScenes: 'Expand scenes for {{label}}',
    collapseEpisodeScenes: 'Collapse scenes for {{label}}',
    expandingScenes: 'Loading scenes…',
    sceneAssetsMissing: 'No reference assets',
    sceneReferences: 'Scene references',
    sceneUploadRef: 'Upload',
    scenePickFromAssets: 'Assets',
    sceneShotCount: '{{count}} shots',
    sceneRefsLoadFailed: 'Failed to load scene references',
    sceneRefsLinkFailed: 'Failed to link assets',
    sceneRefsRemoveFailed: 'Failed to remove reference',
    storyboardStrip: 'Scene storyboard',
    storyboardEmpty: 'Add shots and upload/generate frames to preview here',
    view: 'View',
    viewShotTitle: 'Shot detail',
    noFrameYet: 'No storyboard frame yet',
    addFrame: 'Add frame',
    replaceFrame: 'Replace',
    framePickerTitle: 'Frame · {{slug}}',
    uploadFrame: 'Upload frame',
    pickAssetShort: 'Library',
    edit: 'Edit',
    save: 'Save',
    pickAsset: 'Pick from library · {{slug}}',
    noAssets: 'No storyboard assets in library',
    deleteTitle: 'Delete shot',
    deleteConfirm: 'Delete shot {{slug}}? Associated takes will be removed.',
    professionalTableTitle: 'Storyboard sheet',
    tableRowCount: '{{n}} shots',
    sceneUntitled: 'Untitled scene',
    aiSelectScene: 'Scene',
    aiSceneScript: 'Scene script',
    aiSceneScriptPlaceholder: 'Parse scenes in script editor first',
    aiGenerateScene: 'Generate shots for scene',
    aiScenePrompt:
      'You are a film storyboard artist. From episode {{episodeNo}} scene {{sceneNo}} script below, output a JSON array (shots for this scene only). Each item: scene_no (always {{sceneNo}}), shot_size, camera_angle, camera_movement, description, dialogue (empty if none), sound_note, music_note, duration_sec (default 3), storyboard_note. JSON only.\n\nScene script:\n{{body}}',
    aiPanelTitle: 'AI breakdown',
    aiDraftCount: 'Preview {{n}} drafts',
    aiApplyDrafts: 'Apply {{n}} shots',
    aiFailed: 'AI breakdown failed',
    aiParseFailed: 'Could not parse AI output — retry or adjust prompt',
    breakdownDone: 'Breakdown done: {{created}} created, {{skipped}} skipped',
    breakdownFailed: 'Breakdown failed (lock script revision first)',
    status: {
      draft: 'Draft',
      storyboarded: 'Storyboarded',
      in_progress: 'In progress',
      review: 'Review',
      approved: 'Approved',
    },
  },
  storyboard: {
    title: 'Storyboard',
    subtitle: 'Preferred take thumbnails · shot size · notes',
    empty: 'Add shots and generate takes first',
    noPreview: 'No preview',
    shotSize: 'Size',
    notePlaceholder: 'Framing / movement notes…',
    openWorkbench: 'Open workbench',
    loadFailed: 'Failed to load storyboard',
  },
  bible: {
    title: 'Art bible',
    subtitle: 'Characters · locations · visual style for AI script and image generation',
    subtitleV2: 'Character/location art notes and project visual style for AI workflows',
    refImage: 'Reference',
    refImageHint: 'Upload a reference image; AI visual workspace will support one-click generation later',
    locationRef: 'Location ref',
    uploadRef: 'Upload reference',
    uploadRefFailed: 'Reference upload failed',
    add: 'Add',
    addEntry: 'Add {{kind}}',
    delete: 'Delete',
    deleteCharacterConfirm: 'Delete character "{{name}}"?',
    deleteLocationConfirm: 'Delete location "{{name}}"?',
    deletePropConfirm: 'Delete prop "{{name}}"?',
    deleteCostumeConfirm: 'Delete costume "{{name}}"?',
    searchPlaceholder: 'Search by name…',
    emptyList: 'No {{kind}} yet — use Add above',
    selectHint: 'Select an entry on the left to edit',
    save: 'Save',
    saved: 'Saved',
    colName: 'Name',
    colDescription: 'Description',
    colRef: 'Reference',
    colActions: 'Actions',
    fieldName: 'Name',
    fieldDescription: 'Description',
    fieldDescriptionPlaceholder: 'Appearance, personality, wardrobe, relationships…',
    fieldLocationDescriptionPlaceholder: 'Environment, time of day, mood, props…',
    fieldPromptHint: 'AI prompt snippet',
    fieldPromptHintPlaceholder: 'Keywords appended when generating images or expanding text…',
    newNamePlaceholder: 'Name…',
    saveStyle: 'Save style',
    create: 'Create',
    createAndSave: 'Save to library',
    editEntry: 'Edit {{kind}}',
    modalImages: 'Reference images',
    modalImage: 'Reference image',
    modalImagesHint: 'Upload multiple images or pick from the asset library; optional',
    modalImageHint: 'One reference image only — upload locally or pick from assets',
    uploadImages: 'Upload',
    uploadImage: 'Upload image',
    pickFromAssets: 'From asset library',
    pickAssetsTitle: 'Pick images from library',
    pickAssetsSearch: 'Search assets…',
    pickAssetsEmpty: 'No images yet — upload in Assets or register from canvas',
    pickAssetsConfirm: 'Confirm ({{count}})',
    seriesOutlineRef: 'Series outline',
    seriesOutlineEmpty: 'No series outline yet — edit it on the Script page',
    modalContent: 'Description',
    nameRequired: 'Name is required',
    untitledImage: 'Reference',
    createCharacter: 'Create character',
    createLocation: 'Create location',
    createProp: 'Create prop',
    createCostume: 'Create costume',
    createStyle: 'Create visual',
    assignWork: 'Assign work',
    editEntryShort: 'Edit',
    imagePreview: 'Reference preview',
    imagePreviewCounter: '{{current}} / {{total}}',
    noImages: 'No images',
    noContent: 'No description',
    deleteStyleConfirm: 'Clear project visual style?',
    deleteStyleFieldConfirm: 'Delete “{{name}}”?',
    loadFailed: 'Failed to load',
    saveFailed: 'Failed to save',
    tab: { characters: 'Characters', locations: 'Locations', props: 'Props', costumes: 'Costumes', style: 'Visual style' },
    kindCharacter: 'character',
    kindLocation: 'location',
    kindProp: 'prop',
    kindCostume: 'costume',
    style: {
      sectionTitle: 'Project visual style',
      sectionHint: 'Art direction and prompts applied across shots by default',
      artDirection: 'Art direction',
      artDirectionPlaceholder: 'e.g. cyberpunk, ink wash, desaturated realism…',
      colorPalette: 'Color palette',
      colorPalettePlaceholder: 'Primary, accent, highlight and shadow tendencies…',
      stylePrompt: 'Style prompt',
      stylePromptPlaceholder: 'Style keywords merged into AI image prompts…',
      negativePrompt: 'Negative prompt',
      negativePromptPlaceholder: 'Elements to avoid: blur, watermark, malformed hands…',
    },
  },
  reviews: {
    title: 'Review center',
    subtitle: 'Storyboard shots & assets (character / location / prop / costume / style) · approve / request changes',
    backToProduction: 'Back to project',
    filterLabel: 'Review category',
    filter: {
      all: 'All',
      shot: 'Shots',
      character: 'Characters',
      location: 'Locations',
      prop: 'Props',
      costume: 'Costumes',
      style: 'Style',
    },
    subject: {
      shot: 'Storyboard shot',
      character: 'Character asset',
      location: 'Location asset',
      prop: 'Prop asset',
      costume: 'Costume asset',
      style: 'Style asset',
    },
    queueTitle: 'Queue · {{n}}',
    noPreview: 'No preview',
    videoBadge: 'Video',
    metaEpisode: 'Episode',
    metaShotSize: 'Shot size',
    metaDuration: 'Duration',
    metaScene: 'Scene/Shot',
    metaAssetKind: 'Asset kind',
    metaSubject: 'Category',
    openBible: 'Open art bible',
    assetReviewHint: 'Assets enter this queue after “Submit for review” on canvas; approval marks them ready.',
    assetNoteTitle: 'Review note',
    assetNoteHint: 'Note from submit or request-changes',
    assetNoteEmpty: 'No note',
    commentsHint: 'Frame-level notes · mark resolved · video: frame step & In/Out ranges',
    commentsEmpty: 'No comments yet',
    videoTimeline: 'Review timeline',
    videoIn: 'In',
    videoOut: 'Out',
    videoClearInOut: 'Clear range',
    videoZoomIn: 'Zoom in',
    videoZoomOut: 'Zoom out',
    videoZoomReset: 'Reset',
    videoCommentHint: '←/→ frame step · I/O range · send binds frame or In~Out · Ctrl+wheel zoom',
    commentSegmentPlaceholder: 'Comment on selected range…',
    videoSegmentReady: 'Range {{range}}',
    videoSegmentInOnly: 'In {{time}} · set Out to complete range',
    videoSegmentOutOnly: 'Out {{time}} · set In to complete range',
    empty: 'No reviews yet',
    emptySelectionTitle: 'No item selected',
    emptySelectionHint: 'Pick an item from the queue on the left',
    emptyPreview: 'Preview · select an item to display',
    openComments: '{{n}} open comments',
    submitReview: 'Submit for review',
    approve: 'Approve',
    requestChanges: 'Request changes',
    requestChangesConfirm: 'Confirm return',
    requestChangesSuccess: 'Returned for revision — the team will be notified',
    approveSuccess: 'Review approved — item removed from the queue',
    decisionTitle: 'Review decision',
    decisionHint: 'After frame notes: resolve all comments → Approve; use Request changes for a full return',
    decisionBlocked: '{{n}} open comments remain — resolve them before approving',
    decisionNeedsSubmit: 'Asset must be submitted for review (status Pending) before approve or return',
    rejectDialogHint: 'Add an overall note, or leave blank to bundle unresolved comments above',
    rejectDialogPlaceholder: 'Optional overall feedback…',
    feedbackPlaceholder: 'Feedback for request changes…',
    comments: 'Comments',
    commentPlaceholder: 'Add comment…',
    addComment: 'Send',
    commentSent: 'Comment sent — team members will be notified',
    resolve: 'Resolve',
    reopen: 'Reopen',
    loadFailed: 'Failed to load review queue',
    actionFailed: 'Action failed',
  },
  tasks: {
    title: 'Task center',
    subtitle: 'Take production tasks, deliver work, and earn point rewards',
    empty: 'No open tasks yet',
    myTasks: 'My tasks',
    myTasksSubtitle: 'Track assignments, deliveries, and review feedback',
    myEmpty: 'No assignments yet',
    loginRequiredMyTasks: 'Sign in to view my tasks',
    loginRequiredMyTasksHint: 'Assignment history and delivery status require an authenticated account.',
    loginRequiredTake: 'Sign in to take this task',
    loginRequiredTakeHint: 'Applying or accepting tasks requires a creator account.',
    guestHintTitle: 'Sign in to take tasks',
    guestHint: 'Sign in to apply, submit deliverables, and manage your assignments.',
    backToList: 'Task list',
    points: 'pts',
    slotsLeft: 'Left',
    noSummary: 'No summary yet',
    deadline: 'Due {{date}}',
    cardStatus: {
      published: 'Recruiting',
      in_progress: 'In progress',
      completed: 'Completed',
      draft: 'Draft',
      cancelled: 'Cancelled',
    },
    detail: {
      overview: 'Overview',
      requirements: 'Deliverables',
      mode: 'Accept mode',
      deadlineLabel: 'Deadline',
      noDeadline: 'Open-ended',
      takeHint: 'Slots available — accept to start and submit your deliverable.',
      myAssignment: 'My assignment',
      slotsFull: 'All slots taken',
      slotsFullHint: 'This task has no open slots. Browse other available tasks.',
    },
    reward: 'Reward',
    modeApply: 'Apply to join',
    modeOpen: 'Instant accept',
    apply: 'Apply',
    accept: 'Accept now',
    status: 'Status',
    submit: 'Submit deliverable',
    pickWork: 'Pick a published work (optional)',
    externalUrl: 'External URL (optional)',
    upload: 'Upload attachment',
    note: 'Notes',
    submitBtn: 'Submit',
    boardTitle: 'Task board',
    boardSubtitle: 'In-project work tracking',
    subtitleDual: 'Internal assignments · collaboration bounties · separate economic chains',
    tabInternal: 'Assignments',
    tabBounty: 'Bounties',
    colTitle: 'Title',
    colDescription: 'Description',
    colReward: 'Reward',
    colSlots: 'Slots',
    colReviewNote: 'Review note',
    bountyCreate: 'New bounty',
    bountyCreateTitle: 'Create collaboration bounty',
    bountyCreateInvalid: 'Enter a title and a valid reward amount',
    internalHint: 'Assignments come from bible/shots; status is API-driven (start, submit, review).',
    assignmentColumn: {
      pending: 'Pending',
      in_progress: 'In progress',
      submitted: 'Submitted',
      approved: 'Approved',
    },
    assignmentEmpty: 'No assignments',
    submitAcceptance: 'Submit delivery',
    openReviewCenter: 'Review in center',
    approveAssignment: 'Accept assignment',
    assignmentApproveBlocked: 'Approve all shots in review center first',
    scopeItem: '{{type}} · {{id}}',
    quotaRemaining: 'Quota {{remaining}}/{{total}}',
    changesRequested: 'Changes requested',
    startWork: 'Start',
    submitReview: 'Submit',
    approve: 'Approve',
    requestChanges: 'Request changes',
    reviewNotePlaceholder: 'Review note (optional)',
    loadFailed: 'Failed to load tasks',
    updateFailed: 'Failed to update task',
    deleteFailed: 'Failed to delete task',
    deleteConfirm: 'Delete task "{{title}}"?',
    bountyHint: 'Chain B · publish freezes reward points; accept/submit via task center /api/v1/tasks/:id/*; approve here.',
    bountyTitlePlaceholder: 'Bounty task title',
    bountyDescPlaceholder: 'Description and deliverables',
    bountyReward: 'Reward (points per slot)',
    bountyCreateDraft: 'Create draft',
    bountyPublish: 'Publish & freeze escrow',
    bountyPublishFailed: 'Failed to publish bounty',
    bountyCreateFailed: 'Failed to create bounty',
    bountyApproveFailed: 'Approval failed',
    bountyEmpty: 'No collaboration bounties yet',
    bountyRewardPts: '{{n}} pts',
    bountySlots: '{{taken}}/{{total}} slots',
    bountyAcceptHint: 'Published task ID {{id}} · assignees accept in task center',
    bountyAssignments: 'Assignments',
    bountyNoAssignments: 'No assignments yet',
    bountyApprove: 'Approve',
    column: {
      todo: 'To do',
      in_progress: 'In progress',
      review: 'Review',
      done: 'Done',
    },
    scopeDept: 'Dept · {{name}}',
    scopeShot: 'Shot · {{id}}',
    scopeScene: 'Scene · {{key}}',
    scopeBible: 'Bible · {{type}}',
    scopeAsset: 'Asset · {{id}}',
  },
  assets: {
    title: 'Asset library',
    subtitle: 'All project visual assets · includes unregistered canvas outputs',
    manageHint: 'Upload files or register external URLs here. Visual assets can also be created in the infinite canvas from bible/shots pages.',
    searchPlaceholder: 'Search name or note…',
    viewGrid: 'Grid view',
    viewList: 'List view',
    filterAllKinds: 'All kinds',
    tab: {
      all: 'All',
      image: 'Images',
      video: 'Videos',
      audio: 'Audio',
      other: 'Other',
    },
    empty: 'No assets yet',
    mediaPreview: 'Media preview',
    emptyTab: 'No assets in this category',
    loadFailed: 'Failed to load assets',
    registerFromCanvas: 'Register to library',
    registerFailed: 'Registration failed',
    unregistered: 'Unregistered',
    sourceBibleCharacter: 'Art bible · character',
    sourceBibleLocation: 'Art bible · location',
    sourceShot: 'Shot {{slug}}',
    col: { name: 'Name', kind: 'Kind', source: 'Source', tags: 'Tags', status: 'Status', actions: 'Actions' },
    kind: {
      character_ref: 'Character ref',
      location_ref: 'Location ref',
      storyboard: 'Storyboard',
      take_image: 'Take image',
      take_video: 'Take video',
      prop: 'Prop',
      costume: 'Costume',
      misc: 'Misc',
      other: 'Other',
      image: 'Image',
      video: 'Video',
      audio: 'Audio',
      doc: 'Document',
    },
    status: {
      draft: 'In progress',
      review: 'In review',
      ready: 'Approved',
      archived: 'Archived',
    },
    grantCost: 'Grant cost',
    markReady: 'Mark ready',
    markReadyFailed: 'Failed to mark ready',
    unmarkReady: 'Revert to draft',
    unmarkReadyFailed: 'Failed to revert to draft',
    archive: 'Archive',
    archiveFailed: 'Failed to archive asset',
    audioLoadFailed: 'Audio failed to load. Try registering to the library or regenerate.',
    audioPlay: 'Play',
    audioPause: 'Pause',
    audioPlaybackRate: 'Preview speed',
    viewActive: 'Active',
    viewArchived: 'Archived ({{count}})',
    emptyArchived: 'No archived assets',
    revokeListing: 'Cancel listing',
    revokeListingFailed: 'Failed to cancel listing',
    createListing: 'List on marketplace',
    listingTitle: 'Asset listing (chain C)',
    listingHint: 'Creates a marketplace draft, then opens mycanvas sell tab to submit for review. Revenue goes to project owner.',
    listingPrice: 'Price (points)',
    listingSummary: 'Listing summary',
    listingConfirm: 'Create draft & open sell',
    listingCancel: 'Cancel',
    listingPriceInvalid: 'Enter a valid price',
    listingFailed: 'Failed to create listing',
    listingStatus: 'Listing {{status}}',
    detailTitle: 'Asset details',
    fieldName: 'Name',
    fieldTags: 'Tags',
    fieldNote: 'Note',
    fieldSource: 'Source',
    tagPlaceholder: 'Type a tag and press Enter',
    addTag: 'Add',
    saveMeta: 'Save tags & note',
    saveFailed: 'Save failed',
    upload: 'Upload file',
    uploadFailed: 'Upload failed',
    delete: 'Delete asset',
    deleteConfirm: 'Delete this asset? The stored file will be removed too.',
    deleteFailed: 'Delete failed',
    assignTaskSection: 'Assign production task',
    assignTaskTitleSuffix: 'production',
    assignTaskAssignee: 'Assignee',
    assignTaskPickMember: 'Select member',
    assignTaskTitle: 'Task title',
    assignTaskDesc: 'Description',
    assignTaskDue: 'Due date (optional)',
    assignTaskSubmit: 'Create internal task',
    assignTaskAssigneeRequired: 'Select an assignee',
    assignTaskSuccess: 'Task added to board',
    assignTaskFailed: 'Failed to assign task',
    followUpAssign: 'Follow-up assign',
    linkedTask: 'Linked task',
    linkedTaskStatus: '{{title}} · {{status}}',
  },
  timeline: {
    title: 'Episode timeline',
    subtitle: 'Approved shots · preferred takes · reorder · export · publish',
    episode: 'Ep',
    empty: 'No shots in this episode',
    exportManifest: 'Export manifest',
    syncWorks: 'Sync to works (draft)',
    publishWorks: 'Sync & publish',
    lastExport: 'Last export {{id}}…',
    moveUp: 'Move up',
    moveDown: 'Move down',
    moveLeft: 'Move earlier',
    moveRight: 'Move later',
    prevClip: 'Previous clip',
    nextClip: 'Next clip',
    trackLabel: 'Edit track',
    sequenceLength: 'Sequence {{sec}}s',
    selectClip: 'Click a clip on the track for details',
    clipIndex: 'Clip {{n}} / {{total}}',
    dragToReorder: 'Drag to reorder clips',
    loadFailed: 'Failed to load timeline',
    reorderFailed: 'Failed to reorder',
    exportFailed: 'Export failed (all clips must be approved with media)',
    publishFailed: 'Publish failed (need exportable episode manifest + owner)',
    publishSuccess: 'Synced {{n}} episodes · work {{id}} · status {{status}}',
    stats: {
      duration: 'Approved duration {{sec}}s',
      approved: '{{n}} exportable clips',
      ratio: 'Aspect {{ratio}}',
      clips: '{{n}} clips',
    },
  },
  writer: {
    nav: { script: 'Script' },
    hub: {
      title: 'Writer spaces',
      subtitle: 'Solo script workspaces, separate from productions',
      emptyTitle: 'No writer spaces yet',
      empty: 'Create your first writer space to start drafting',
      createFirst: 'Create first space',
      create: 'New space',
      count: '{{n}} spaces',
      defaultTitle: 'Untitled space {{n}}',
      open: 'Open',
      delete: 'Delete space',
      deleteConfirm: 'Delete this writer space? All scripts will be removed.',
      loadFailed: 'Failed to load writer spaces',
      deleteFailed: 'Delete failed',
      colTitle: 'Title',
      colType: 'Type',
      colEpisodes: 'Target eps',
      colUpdated: 'Updated',
    },
    create: {
      title: 'New writer space',
      submit: 'Create',
      fieldTitle: 'Title',
      fieldSummary: 'Summary',
      titleRequired: 'Title is required',
      failed: 'Create failed',
    },
    script: {
      title: 'Writer workbench',
      subtitle: 'Manage script documents · solo writing · separate from productions',
      defaultWorkspaceTitle: 'My scripts',
      count: '{{n}} scripts',
      emptyTitle: 'No scripts yet',
      empty: 'Create your first script document',
      createFirst: 'Create first script',
      create: 'New script',
      open: 'Edit',
      delete: 'Delete script',
      deleteConfirm: 'Delete this script? All episode content will be removed.',
      loadFailed: 'Failed to load scripts',
      createFailed: 'Failed to create script',
      deleteFailed: 'Delete failed',
      colTitle: 'Title',
      colEpisodes: 'Target eps',
      colUpdated: 'Updated',
    },
    editor: {
      backList: 'Back to scripts',
      episodeLabel: 'Ep {{n}}',
      episodeTitle: 'Episode title',
      outline: 'Outline',
      body: 'Body',
      loadFailed: 'Failed to load script',
      saveFailed: 'Save failed',
      statusSaved: 'Saved',
      statusSaving: 'Saving…',
      statusDirty: 'Unsaved',
      statusError: 'Save failed',
      sidePanel: 'Notes / versions',
      aiTitle: 'AI assist',
      aiPlaceholder: 'e.g. polish dialogue, expand scene 2…',
      aiSuggest: 'Suggest',
      aiApply: 'Apply to draft',
      aiApplySeriesOutline: 'Write series outline',
      aiApplyStoryContent: 'Write story content',
      aiApplyEpisodeOutline: 'Write episode beats',
      aiApplySceneList: 'Append scene list to draft',
      aiApplyNextScene: 'Add as new scene',
      aiFailed: 'AI assist failed',
      agentTitle: 'Writer Agent',
      agentLayer: {
        series: 'Series outline · Agent',
        story: 'Story content · Agent',
        episode: 'Episode beats & scenes · Agent',
      },
      agentEmpty: {
        series: 'This station writes only the series bible: theme, characters, spine.\nNo seasons, episodes, scenes, or per-episode beats. Click Write series outline, then Send.',
        story: 'This station writes only story prose.\nYou can write without a series outline, but it may be loosely structured. It will not split scenes.',
        episode: 'Write the episode beats first, then split scenes.\nYou cannot write an episode without a series outline or story, and you cannot continue a scene without episode beats.',
      },
      agentRoleUser: 'You',
      agentRoleAssistant: 'Agent',
      agentThinking: 'Thinking…',
      agentInputTitle: 'Instruction',
      agentInputPlaceholder: {
        series: 'e.g. ensemble, the lead goes from restraint to revenge… (Ctrl+Enter to send)',
        story: 'e.g. start with her return home and lay out the relationships… (Ctrl+Enter to send)',
        episode: 'e.g. write a night interior scene where the lead finds a clue… (Ctrl+Enter to send)',
      },
      agentSend: 'Send',
      agentClearChat: 'Clear chat',
      agentClearChatTitle: 'Clear Agent chat',
      agentClearChatConfirm: 'This removes all Agent messages for this station and cannot be undone.',
      agentApplyDraftOnly: 'Finalized episodes: copy results manually. Apply writes draft episodes only.',
      agentApplySeriesOutlineMissing: 'Cannot write series outline from this page.',
      agentApplyStoryContentMissing: 'Cannot write story content from this page.',
      agentApplyEpisodeOutlineMissing: 'Cannot write episode beats from this page.',
      agentStoryNoOutlineHint: 'There is no series outline yet. You can still write the story, but it may be loosely structured. Add the outline afterwards if you can.',
      agentNeedSeriesOrStory: 'There is no series outline or story yet. Write one of them before episode beats and scenes.',
      agentNeedEpisodeOutline: 'There are no episode beats yet. Write what this episode must do before the next scene or scene list.',
      agentFinalizedHint: 'Finalized: copy Agent output into your working copy, then save.',
      agentDefaultSeries: 'Write or refine the series outline',
      agentDefaultStory: 'Write or continue the story',
      agentDefaultEpisodeOutline: 'Write this episode’s beats',
      agentDefaultContinue: 'Write the next empty scene and add it to the tree',
      agentDefaultSceneList: 'List scene headings only, no dialogue',
      agentDefaultExpand: 'Continue with the next scene',
      agentDefaultCheck: 'Check consistency with the series bible',
      agentSkill: {
        series_outline: 'Write series outline',
        story_content: 'Write story',
        episode_outline: 'Write episode beats',
        continue_scene: 'Next scene',
        episode_scene_list: 'Episode scene list',
        polish: 'Polish',
        expand_outline: 'Next scene',
        continuity_check: 'Continuity',
        freeform: 'Freeform',
      },
    },
    import: {
      short: 'Import to production',
      title: 'Import to production',
      subtitle: 'Import this script for production analysis ({{n}} episode(s))',
      production: 'Target production',
      targetScript: 'Target script',
      targetScriptId: 'Target script ID',
      scriptN: 'Script {{n}}',
      productionRequired: 'Select a production',
      scriptRequired: 'Select a target script',
      noProductions: 'No productions available to import into',
      noScripts: 'This production has no scripts yet — create one in the production project first',
      forbidden: 'Import not allowed or writer import is unavailable',
      sourceNotFound: 'Writer script not found or not accessible',
      scriptNotFound: 'Target script not found',
      submit: 'Import',
      modeFork: 'Copy into project',
      modeAttach: 'Link to project',
      scopeCurrent: 'Current page',
      scopeAll: 'All pages',
      partsTitle: 'Import layers',
      partSeriesOutline: 'Series outline',
      partStoryContent: 'Story',
      partSeasons: 'Seasons',
      partEpisodes: 'Episodes',
      partScenes: 'Scenes',
      partImported: 'Imported',
      partNoSource: 'Nothing to import',
      partsRequired: 'Select at least one layer to import',
      partsHint: 'Already imported layers stay optional; checking them overwrites drafts.',
      attachSubtitle: 'Link the selected layers to a production script (ep {{n}}); episode edits write back to your personal draft',
      attachSuccess: 'Linked: {{imported}}; skipped: {{skipped}}',
      success: 'Imported: {{imported}}; skipped: {{skipped}}',
      failed: 'Import failed',
    },
    listing: {
      hint: 'Locked Rev {{rev}} · creates a marketplace draft then opens sell page',
    },
  },
  creative: {
    hub: {
      title: 'Creative canvas',
      subtitle: 'Infinite canvas workspaces, separate from production and writer',
      emptyTitle: 'No creative spaces yet',
      empty: 'Create your first space to explore on the canvas',
      createFirst: 'Create first space',
      create: 'New space',
      count: '{{n}} spaces',
      defaultTitle: 'Untitled space {{n}}',
      open: 'Open canvas',
      delete: 'Delete space',
      deleteConfirm: 'Delete this creative space? Canvas content will be removed.',
      loadFailed: 'Failed to load creative spaces',
      deleteFailed: 'Delete failed',
      colTitle: 'Title',
      colUpdated: 'Updated',
    },
    library: {
      nav: 'Creative library',
      workspaces: 'Spaces',
      assets: 'Assets',
    },
    assets: {
      searchPlaceholder: 'Search filename…',
      upload: 'Upload asset',
      emptyTitle: 'No personal assets yet',
      empty: 'Images, videos, and audio you upload go to your personal library. All creative spaces share it; production projects stay isolated.',
      loadFailed: 'Failed to load assets',
      delete: 'Delete',
      deleteTitle: 'Delete asset',
      deleteConfirm: 'Delete “{{name}}”? The stored file will be removed.',
      deleteFailed: 'Delete failed',
    },
    create: {
      title: 'New creative space',
      editTitle: 'Edit creative space',
      submit: 'Create',
      saved: 'Space updated',
      saveFailed: 'Failed to save',
      fieldTitle: 'Title',
      fieldSummary: 'Summary',
      titleRequired: 'Title is required',
      failed: 'Create failed',
    },
    listing: {
      short: 'List',
      title: 'List canvas',
      hint: 'Pick generated clips as storefront samples. Empty canvases cannot be listed.',
      fieldClips: 'Storefront clips',
      emptyClips: 'No generated image or video yet. Finish a generation before listing.',
      selectAtLeastOne: 'Select at least one clip as a storefront sample',
      selectedCount: '{{n}} / {{total}} selected',
      disabledHint: 'This canvas revision is already listed — manage it in Trading Center',
      fieldTitle: 'Listing title',
      fieldPrice: 'Price (points)',
      fieldSummary: 'Summary (optional)',
      submit: 'Create draft & go to sell',
      cancel: 'Cancel',
      priceInvalid: 'Enter a valid price',
      failed: 'Failed to create listing',
    },
    publish: {
      title: 'Choose clips to publish',
      hint: 'Select which generated clips to sync as work episodes. By default only the current take per node is selected — not the full generation history.',
      empty: 'No publishable video or image yet. Complete a generation on the canvas first.',
      cancel: 'Cancel',
      selectAtLeastOne: 'Select at least one clip',
      selectedCount: '{{n}} / {{total}} selected',
      mediaVideo: 'Video',
      mediaImage: 'Image',
      preferred: 'Current take',
    },
  },
  canvasBoards: {
    title: 'Canvases',
    subtitle: 'Manage free-form AI workflow boards; each canvas stores its own nodes and outputs.',
    subtitleCount: '{{count}} canvas(es) · each board saves nodes and generations independently',
    create: 'New canvas',
    createTitle: 'New canvas',
    rename: 'Rename',
    renameTitle: 'Rename canvas',
    open: 'Open canvas',
    defaultBadge: 'Default',
    rowHint: 'Free-form AI image / video workflow',
    fieldTitle: 'Canvas name',
    fieldTitlePlaceholder: 'e.g. Concept exploration A',
    col: {
      title: 'Name',
      updated: 'Updated',
      actions: 'Actions',
    },
    emptyTitle: 'No canvases yet',
    emptyHint: 'Create your first board to start arranging AI image workflows.',
    loadFailed: 'Failed to load canvas list',
    createFailed: 'Failed to create canvas',
    renameFailed: 'Failed to rename canvas',
    deleteFailed: 'Failed to delete canvas',
    deleteTitle: 'Delete canvas',
    deleteConfirm: 'Delete “{{name}}”? All nodes and generations in this board will be removed permanently.',
  },
  promptLibrary: {
    title: 'Prompt library',
    tabTenant: 'Public',
    tabMine: 'Mine',
    allCategories: 'All',
    uncategorized: 'Uncategorized',
    copy: 'Copy',
    copied: 'Copied to clipboard',
    copyFailed: 'Copy failed',
    loadFailed: 'Failed to load',
    saveFailed: 'Failed to save',
    deleteFailed: 'Failed to delete',
    uploadFailed: 'Upload failed',
    tenantEmpty: 'No public prompts yet — ask your admin to configure them',
    mineEmpty: 'No personal prompts yet — create one above',
    createPrompt: 'New prompt',
    editPrompt: 'Edit prompt',
    createCategory: 'New category',
    categoryNamePlaceholder: 'Category name',
    deletePromptTitle: 'Delete prompt',
    deletePromptConfirm: 'Delete “{{name}}”?',
    fieldCategory: 'Category',
    fieldName: 'Name',
    fieldContent: 'Prompt',
    fieldRemark: 'Notes',
    fieldEffectMedia: 'Effect preview',
    mediaNone: 'None',
    mediaImage: 'Image',
    mediaVideo: 'Video',
    uploadEffect: 'Upload',
    colName: 'Name',
    colPrompt: 'Prompt (click to expand)',
    colRemark: 'Notes',
    colThumb: 'Thumb',
    colAction: 'Action',
    applyPrompt: 'Copy prompt',
  },
  canvas: {
    back: 'Back',
    untitled: 'Visual canvas',
    viewport: {
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fitView: 'Fit to canvas',
      resetZoom: 'Reset to 100%',
      selectionHint: 'Drag on empty area to select · Hold Space to pan · Scroll to zoom',
    },
    minimap: {
      ariaLabel: 'Canvas minimap — drag to pan, scroll to zoom, click to jump',
    },
    saving: 'Saving…',
    save: 'Save',
    saved: 'Saved',
    unsaved: 'Unsaved',
    saveFailed: 'Save failed',
    conflict: 'Conflict',
    conflictHint: 'This canvas was saved in another window or tab. Reload discards local changes; overwrite replaces the server copy with yours.',
    conflictReload: 'Reload',
    conflictReloadTitle: 'Reload canvas',
    conflictReloadConfirm: 'Discard local unsaved changes and load the latest version from the server?',
    conflictOverwrite: 'Overwrite',
    conflictOverwriteTitle: 'Overwrite server version',
    conflictOverwriteConfirm: 'Replace the newer server version with your current canvas. Changes in other windows will be lost.',
    registerReturn: 'Register & return',
    run: 'Generate',
    runRunning: 'Generating',
    upstream: 'Upstream',
    upstreamRefs: 'Upstream refs',
    previewEmpty: 'No preview',
    promptPositive: 'Positive prompt',
    promptPositivePlaceholder: 'Visual description, motion, mood… (optional — auto-merges upstream prompts)',
    promptNegative: 'Negative prompt',
    promptNegativePlaceholder: 'Elements to avoid: blurry, watermark, deformed hands…',
    characterHintPlaceholder: 'Face, hair, outfit consistency…',
    contextEmpty: 'From page context or edit manually',
    textPlaceholder: 'Narration, script snippet, notes…',
    textInstructionPlaceholder: 'Describe what to generate, e.g. a cyberpunk rainy street scene prompt…',
    textOutputPlaceholder: 'Generated text appears here; edit manually before connecting downstream',
    refNamePlaceholder: 'Character / scene name',
    refHintPlaceholder: 'Consistency keywords, style notes…',
    refAddImage: 'Add reference image',
    model: 'Model',
    modelCost: '{{cost}} pts',
    noModel: 'No model configured for this type — enable in tenant AI center',
    history: 'History',
    historyHint: 'Click a version to preview and focus its node; star = preferred take, green icon = workspace cover; preferred or cover versions cannot be deleted',
    historyEmpty: 'No generations yet',
    historyAllNodes: 'All generation nodes',
    historyDelete: 'Delete this version',
    historyDeleteTitle: 'Delete history version',
    historyDeleteConfirm: 'Delete this generation record? This cannot be undone.',
    historyDeleteBlockedPreferred: 'Preferred take cannot be deleted — switch preferred first',
    historyDeleteBlockedCover: 'Workspace cover cannot be deleted — change cover first',
    historySetCover: 'Set as workspace cover',
    historyWorkspaceCover: 'Workspace cover',
    historyCoverSet: 'Set as workspace cover',
    historyCoverSetFailed: 'Failed to set cover',
    connected: '{{n}} upstream',
    ready: 'Ready',
    registerName: 'Asset name',
    uploadHint: 'Click to upload image / video / audio',
    uploaded: 'Uploaded',
    section: {
      inputs: 'Upstream inputs',
      context: 'Context',
      prompt: 'Prompt',
      text: 'Text output',
      instruction: 'Instruction',
      negative: 'Negative prompt',
      model: 'Model',
      params: 'Parameters',
      references: 'References',
      identity: 'Name & notes',
      output: 'Registration',
      assetKind: 'Asset kind',
    },
    exportHint: 'Connect an upstream video, or upload an external cut. Add audio to mux.',
    exportHintVideoOnly: 'Video connected. Adopt it as-is, or add audio and compose.',
    exportEmpty: 'Connect a video or upload an external cut',
    exportTitle: 'Film title',
    exportTitlePlaceholder: 'Delivery title',
    exportUpload: 'Upload cut',
    exportAdopt: 'Use video',
    exportDone: 'Ready to sync to Works',
    exportRunning: 'Processing cut…',
    exportCompose: 'Compose export',
    registerCreativeHint: 'Register is for production asset libraries. In creative mode, use Composite export to upload or publish the final cut.',
    audioTrim: {
      start: 'Trim start (s)',
      end: 'Trim end (s)',
      action: 'Trim',
      hint: 'Trim the current preview into a new upload node (WAV)',
      nodeName: '{{name}}_trim',
    },
    dockTitle: 'Add node',
    dockCollapse: 'Collapse',
    dockAdd: 'Nodes',
    dockLibrary: 'Asset library',
    dockLibraryHint: 'Uploads and generations stay in the library; deleting a canvas node does not remove them. Click an item to place it back. Manage or delete unused assets on the library page.',
    dockLibraryEmpty: 'No assets yet. Uploads and generations will appear here.',
    dockLibraryOnCanvas: 'On canvas',
    dockLibraryPlace: 'Place on canvas',
    dockLibraryManage: 'Manage library',
    templates: 'All nodes',
    groupCreate: 'Group',
    groupUngroup: 'Ungroup',
    edgeDelete: 'Delete connection',
    groupShortcut: 'Ctrl+G to group',
    ungroupShortcut: 'Ctrl+Shift+G to ungroup',
    category: {
      input: 'Input',
      generate: 'Generate',
      output: 'Output',
      container: 'Container',
    },
    status: {
      idle: 'Idle',
      running: 'Running',
      succeeded: 'Done',
      failed: 'Failed',
    },
    node: {
      context: 'Context',
      text: 'Text',
      prompt: 'Prompt',
      upload: 'Upload',
      refCharacter: 'Character ref',
      refScene: 'Scene ref',
      genImage: 'Image gen',
      genVideo: 'Video gen',
      genAudio: 'Audio gen',
      register: 'Register',
      export: 'Export',
      group: 'Group',
    },
    audioTab: {
      dubbing: 'Dubbing',
      voiceSource: 'Voice source',
      voiceSourceSystem: 'System voice',
      voiceSourceClone: 'Clone',
      voiceSourceDesign: 'Design',
      narration: 'Narration',
      voiceClone: 'Clone',
      voiceDesign: 'Design',
      music: 'Music',
      promptLabel: 'Script',
      promptLabelMusic: 'Music prompt',
      promptLabelLyrics: 'Lyrics',
      promptDubbing: 'Enter narration or dialogue to speak…',
      promptSeedAudio: 'Describe dialogue, ambience, and music, e.g. In a morning café, a woman softly says “hello”…',
      promptElevenTTS: 'Enter narration or dialogue to speak (up to 5,000 characters)…',
      promptElevenMusic: 'Describe genre, mood, instruments, and scene…',
      promptMurekaDescribe: 'Describe genre, mood, and scene (up to 1,024 characters)…',
      promptMurekaLyrics: 'Enter lyrics (up to 1,024 characters)…',
      promptClone: 'Enter text to speak with the cloned voice…',
      promptDesign: 'Enter text to speak with the designed voice…',
      promptMusicDescribe: 'Describe genre, mood, instruments, scene… (or use Style below)',
      promptLyrics: 'Enter lyrics; set style in the params below',
      unavailable: 'No models in this category',
      noModelForTab: 'No models for this category—switch voice source or ask admin',
      hintSeedAudio: 'Seed Audio 1.0: one prompt for dialogue, ambience, and music. Connect up to 3 reference clips.',
      hintMiniMax: 'MiniMax Speech 2.8: pick a voice, then adjust speed / pitch / volume and timbre effects.',
      hintElevenTTS: 'Eleven V3: pick an official voice; stability can be lively, natural, or steady (5,000-character limit).',
      hintElevenMusic: 'Eleven Music V3: choose duration, then generate from the prompt.',
      hintMureka: 'Mureka V8: describe or paste lyrics; turn on Instrumental in more settings. Async generation.',
      hintClone: 'Clone requires enrolled voice; IndexTTS needs reference audio upstream',
      hintMusic: 'Music is async—usually 1–5 minutes',
      hintMusicDescribe: 'Describe-to-music: set style / prompt for instrumental or ambient tracks',
      hintMusicLyrics: 'Lyrics-to-music: enter lyrics for a song; style is optional',
      needRefAudio: 'Connect a reference audio node before generating',
      needDesignedVoice: 'Enter a voice-design ID (create via DashScope voice-design first)',
      createDesignedVoice: 'Create designed voice from description',
      creatingDesignedVoice: 'Creating voice…',
      createDesignedVoiceFailed: 'Failed to create designed voice',
    },
    seedAudio: {
      preset: 'Language, sample rate, and format',
      advanced: 'Advanced settings',
      promptLimitHint: 'Prompt limit 2,000 characters',
    },
    minimaxSpeech: {
      basic: 'Basic adjustments',
      voiceEffects: 'Voice effect adjustments',
      reset: 'Reset',
      advancedToggle: 'Toggle adjustment sections',
      promptLimitHint: 'Prompt limit 50,000 characters',
      modifyPitchHint: 'Pitch: lower = deeper, higher = brighter',
      modifyIntensityHint: 'Intensity: lower = softer, higher = stronger',
      modifyTimbreHint: 'Timbre: lower = fuller, higher = clearer',
    },
    minimaxVoiceLib: {
      title: 'Voice selection',
      tabLibrary: 'Voice library',
      tabMine: 'My voices',
      tabFavorites: 'Favorites',
      searchPlaceholder: 'Search voice library',
      select: 'Select',
      selected: 'Selected',
      favorite: 'Favorite',
      empty: 'No matching voices',
      mineEmpty: 'Cloned voices appear here after MiniMax voice-cloning API is connected.',
      genderMale: 'Male',
      genderFemale: 'Female',
      pageSize: '{{count}} / page',
      total: '{{count}} total',
    },
    murekaMusic: {
      modeTitle: 'Generation mode',
      advanced: 'Advanced settings',
      promptLimitHint: 'Prompt limit 1,024 characters',
    },
    registerKind: {
      characterRef: 'Character ref',
      locationRef: 'Scene ref',
      storyboard: 'Storyboard',
      takeVideo: 'Take video',
      prop: 'Prop',
      misc: 'Other',
    },
    params: {
      batchCount: 'Batch count',
      batch1: '1 image',
      batch2: '2 images',
      batch4: '4 images',
      aspect: 'Aspect',
      aspect916: '9:16',
      aspect169: '16:9',
      aspect11: '1:1',
      aspect43: '4:3',
      aspect34: '3:4',
      resolution: 'Resolution',
      more: 'More parameters',
      expand: 'Expand',
      collapse: 'Collapse',
      steps: 'Steps',
      cfg: 'CFG',
      seed: 'Seed',
      strength: 'Strength',
      denoise: 'Denoise',
      controlnet: 'ControlNet',
      cnNone: 'Off',
      cnCanny: 'Canny',
      cnOpenpose: 'OpenPose',
      cnDepth: 'Depth',
      duration: 'Duration',
      musicDuration: 'Music duration',
      duration30s: '30s',
      duration1m: '1 min',
      duration2m: '2 min',
      duration3m: '3 min',
      duration4m: '4 min',
      duration3: '3s',
      duration5: '5s',
      duration10: '10s',
      duration15s: '15s',
      duration20s: '20s',
      duration25s: '25s',
      motion: 'Motion',
      motionLow: 'Low',
      motionMedium: 'Med',
      motionHigh: 'High',
      withAudio: 'Generate audio',
      motionStrength: 'Motion strength',
      fixFirstFrame: 'Fix first frame',
      fixLastFrame: 'Fix last frame',
      fps: 'FPS',
      yes: 'Yes',
      no: 'No',
      audioMode: 'Audio mode',
      audioModeTts: 'Voice / TTS',
      audioModeBgm: 'BGM',
      audioModeSfx: 'SFX',
      voice: 'Voice',
      voiceCloneId: 'Cloned voice ID',
      designedVoiceId: 'Designed voice ID',
      voicePrompt: 'Voice description',
      instructions: 'Style instructions',
      lyrics: 'Lyrics',
      musicStyle: 'Music style',
      musicMode: 'Generate mode',
      musicModeDescribe: 'Describe → music',
      musicModeLyrics: 'Lyrics → music',
      instrumental: 'Instrumental',
      voiceCloneEmpty: 'No cloned voices yet—enroll in DashScope, or connect reference audio for IndexTTS',
      voiceCloneEmptyEnroll: 'Reference audio connected, but no cloned voices listed—enroll a clone in DashScope, or switch to IndexTTS',
      voiceCloneWrongKind: 'This is a voice-design ID; clone tab requires a cloned (VC) voice—switch to Design tab or enroll a clone in DashScope',
      designedVoiceEmpty: 'No designed voices yet—enter a description and click Create',
      voiceDesignWrongKind: 'This voice is not a design (VD) ID—create or pick one on the Design tab',
      language: 'Language',
      langZh: 'Chinese',
      langEn: 'English',
      sampleRate: 'Sample rate',
      outputFormat: 'Output format',
      voiceMale: 'Male',
      voiceFemale: 'Female',
      voiceNarrator: 'Narrator',
      voiceChild: 'Child',
      speed: 'Speed',
      pitch: 'Pitch',
      pitchTone: 'Tone',
      volume: 'Volume',
      modifyPitch: 'Brightness',
      modifyIntensity: 'Intensity',
      modifyTimbre: 'Timbre',
      soundEffects: 'Sound effect',
      sfxNone: 'None',
      sfxSpaciousEcho: 'Spacious echo',
      sfxAuditorium: 'Hall broadcast',
      sfxTelephone: 'Telephone',
      sfxRobotic: 'Robotic',
      mmVoiceQingse: 'Youthful young man',
      mmVoiceJingying: 'Elite young man',
      mmVoiceBadao: 'Overbearing young man',
      mmVoiceDaxuesheng: 'College student',
      mmVoiceShaonv: 'Young girl',
      mmVoiceYujie: 'Mature woman',
      mmVoiceChengshu: 'Sophisticated woman',
      mmVoiceTianmei: 'Sweet woman',
      mmVoicePresenterMale: 'Male presenter',
      mmVoicePresenterFemale: 'Female presenter',
      mmVoiceAudiobookMale: 'Male audiobook',
      mmVoiceAudiobookFemale: 'Female audiobook',
      mmVoiceCleverBoy: 'Clever boy',
      mmVoiceLovelyGirl: 'Lovely girl',
      stability: 'Stability',
      stabilityCreative: 'Lively',
      stabilityNatural: 'Natural',
      stabilityRobust: 'Steady',
      elVoiceSusan: 'Susan — clear, calm narrator (official)',
      elVoiceRachel: 'Rachel — steady narrator (official)',
      elVoiceSarah: 'Sarah — soft female (official)',
      elVoiceGeorge: 'George — steady male (official)',
      elVoiceLily: 'Lily — clear female (official)',
      elVoiceCharlie: 'Charlie — natural male (official)',
      elVoiceBrian: 'Brian — calm narrator (official)',
      elVoiceAlice: 'Alice — clear female (official)',
    },
    handle: {
      roleIn: 'Input',
      roleOut: 'Output',
      type: {
        text: 'Text',
        image: 'Image',
        video: 'Video',
        audio: 'Audio',
        ctx: 'Context',
      },
      in: {
        text: 'Text / prompt input for generation',
        image: 'Reference image input',
        imageMultiple: 'Reference images (multiple connections)',
        video: 'Video asset input',
        audio: 'Reference audio input',
        audioMultiple: 'Reference audio (multiple connections)',
        ctx: 'Scene context input',
      },
      out: {
        text: 'Text output → connect to generators',
        image: 'Image output → generators or register',
        imageMultiple: 'Image output (fan-out to multiple nodes)',
        video: 'Video output → generators or export',
        audio: 'Audio output → generators or export',
        ctx: 'Scene context output',
      },
    },
    theme: {
      openPicker: 'Style',
      pickerTitle: 'Canvas visual style',
      pickerSub: 'Pick node theme and edge animation; saved locally',
      nodeSection: 'Node themes',
      edgeSection: 'Edge animations',
      selected: 'Active',
      node: {
        zen: { name: 'Zen minimal', desc: 'Thin borders, calm spacing' },
        blueprint: { name: 'Blueprint', desc: 'Grid lines, monospace labels' },
        slate: { name: 'Slate corporate', desc: 'Neutral gray layered card' },
        terminal: { name: 'Terminal', desc: 'Monospace green console' },
        brutal: { name: 'Brutalist', desc: 'Heavy borders, hard shadow' },
        film: { name: 'Film slate', desc: 'Clapper bars, cinema black' },
        notebook: { name: 'Notebook', desc: 'Ruled lines, margin stripe' },
        swiss: { name: 'Swiss grid', desc: 'Red bar, white editorial grid' },
        outline: { name: 'Outline', desc: 'Hollow stroke, light fill' },
        compact: { name: 'Compact', desc: 'Dense layout, small padding' },
        panel: { name: 'Control panel', desc: 'Section labels, dashboard' },
        editorial: { name: 'Editorial', desc: 'Serif titles, magazine feel' },
      },
      edge: {
        solid: { name: 'Solid', desc: 'Static line, no motion' },
        flowStream: { name: 'Flow pulse', desc: 'Recommended · bright dashes stream along the path' },
        flowComet: { name: 'Comet trail', desc: 'Bright segment sweeps quickly along the edge' },
        flowNeon: { name: 'Neon dual', desc: 'Two colors flowing in opposite directions' },
        dashFlow: { name: 'Dash flow', desc: 'Classic forward dash (enhanced)' },
        dashSlow: { name: 'Slow dash', desc: 'Relaxed pacing' },
        dashFast: { name: 'Fast dash', desc: 'High-frequency flow' },
        dashReverse: { name: 'Reverse dash', desc: 'Backward motion' },
        dotMarch: { name: 'Dot march', desc: 'Rounded dots advancing' },
        dotSparse: { name: 'Sparse dots', desc: 'Wide-spaced dots' },
        morse: { name: 'Morse', desc: 'Long/short pattern' },
        snake: { name: 'Snake', desc: 'Long segment crawl' },
        doubleTrack: { name: 'Double track', desc: 'Main + counter rail' },
        braid: { name: 'Braid', desc: 'Two interleaved layers' },
        rail: { name: 'Rail', desc: 'Center solid + side dashes' },
        packet: { name: 'Packet', desc: 'Pulse traveling on path' },
        beads: { name: 'Beads', desc: 'Multi-dot chain' },
        wave: { name: 'Wave', desc: 'Variable dash rhythm' },
        tick: { name: 'Tick', desc: 'Fine rapid marks' },
        step: { name: 'Step', desc: 'Stepped jumps' },
        gradientShift: { name: 'Gradient shift', desc: 'Color drift along path' },
        crossDash: { name: 'Cross dash', desc: 'Irregular segments' },
        pipe: { name: 'Pipe', desc: 'Thick round-cap slow' },
        heartbeat: { name: 'Heartbeat', desc: 'Opacity pulse' },
        lane: { name: 'Lane', desc: 'Wide center + edge marks' },
      },
    },
    errors: {
      loadFailed: 'Failed to load canvas',
      saveFailed: 'Failed to save canvas',
      conflict: 'Canvas was modified elsewhere',
      generateFailed: 'Generation failed',
      providerUnavailable: 'AI service is temporarily unavailable — retry later or switch models',
      providerMemoryOverload: 'AI gateway memory is overloaded — retry later or ask an admin to scale capacity',
      providerCpuOverload: 'AI gateway CPU is saturated — retry later or reduce concurrent jobs',
      providerRelayFailed: 'The gateway received the image but the connection dropped before delivery — retrying automatically',
      seedAudioVoiceMismatch: 'Seed Audio does not use MiniMax voice IDs — refresh and re-select seed-audio-1.0',
      requestTimeout: 'Request timed out, please retry',
      pollTimeout: 'Generation is taking longer than the UI wait limit; the task may still be running—refresh or retry later',
      pollTimeoutAudio: 'Audio synthesis is taking longer than the UI wait limit; the task may still be running—refresh or retry later',
      videoCopyrightBlocked: 'The upstream model blocked this request due to possible copyright or restricted content — rephrase your prompt (avoid famous IP, landmarks, brands) and retry',
      streamNotSupported: 'This model does not support streaming text. Use the regular generate endpoint.',
      noPrompt: 'Enter a prompt or connect a Prompt node',
      noTextInstruction: 'Enter an instruction or connect an upstream text node',
      noModel: 'No AI model configured for this type',
      noRegister: 'Select a register node or complete the chain',
      reviewNoRegisterOutput: 'Add register nodes with generated output before submitting review',
      noOutput: 'No output yet — generate first',
      noImageOutput: 'Generation finished but no image was returned — retry or switch models',
      registerFailed: 'Failed to register asset',
      uploadFailed: 'Upload failed',
      uploadNoUrl: 'Upload succeeded but no asset URL returned',
      uploadCreativeUnsupported: 'Asset upload to production library is not available in creative mode',
      registerCreativeUnsupported: 'Register back to production is not available in creative mode',
      exportNeedVideo: 'Connect an upstream video, or upload a final cut on this node',
      exportNeedVideoAudio: 'Connect upstream video and audio nodes first',
      exportComposeFailed: 'A/V compose failed — check media URLs and browser support',
      audioTrimNoSource: 'No audio preview to trim on this node',
      audioTrimFailed: 'Audio trim failed',
    },
    openFromBible: 'Generate ref in canvas',
    openFromShot: 'Canvas storyboard',
    openFromWorkbench: 'Advanced canvas',
    workbenchDupTitle: 'Some context already exists',
    workbenchDupMessage: 'This shot selection already has a merged context node on this scene canvas. Skip keeps it; Update rewrites the prompt from the latest storyboard.',
    workbenchDupReplace: 'Update existing context',
    workbenchDupSkip: 'Skip, keep existing',
    workbenchDupBatch: 'A merged context for this selection already exists',
  },
  invite: {
    title: 'Join production',
    subtitle: 'You have been invited to collaborate',
    expires: 'Link expires {{date}}',
    accept: 'Accept and open project',
    accepting: 'Accepting…',
    acceptFailed: 'Failed to accept invite',
    loadFailed: 'Invite invalid or expired',
    notFound: 'Invite not found or no longer valid',
    loginToAccept: 'Sign in to accept',
    loginOther: 'Sign in with another account',
  },
  marketplace: {
    title: 'SilkReels Marketplace',
    subtitle: 'Trade scripts, assets, and licensed series across tenants (points only)',
    filterAll: 'All types',
    filterType: 'Listing type',
    typeLicensedWork: 'Licensed series',
    typeScriptProject: 'Script project',
    typeAssetBundle: 'Asset bundle',
    typeStudioScript: 'Studio script',
    typeStudioAsset: 'Studio asset',
    typeStudioCreative: 'Creative canvas',
    empty: 'No listings yet',
    myListings: 'My listings',
    myPurchases: 'My purchases',
    recentTrades: 'Recent trades',
    recentTradesCongrats: 'Recent sales — congrats to these creators',
    recentTradesEmpty: 'No trades yet',
    tradeCongratsShort: 'Sold!',
    tradeMarqueePoints: '{{points}} pts',
    soldBadge: 'Hot sale',
    soldBadgeTimes: '×{{count}} sold',
    soldCount: '{{count}} sold',
    tradeAt: '{{title}} · {{points}} pts',
    backCatalog: 'Back to catalog',
    loginRequiredPurchases: 'Sign in to view purchases',
    loginRequiredPurchasesHint: 'Order history requires an authenticated account.',
    loginRequiredListings: 'Sign in to manage listings',
    loginRequiredListingsHint: 'Creating and managing marketplace listings requires a creator account.',
    loginRequiredPurchase: 'Sign in to purchase',
    loginRequiredPurchaseHint: 'Purchasing with points requires an authenticated account.',
    points: 'points',
    exclusive: 'Exclusive license',
    nonExclusive: 'Non-exclusive',
    buy: 'Buy with points',
    confirmBuyTitle: 'Confirm purchase',
    confirmBuy: 'Confirm and spend points',
    confirmBuyHint: 'Tippable points will be deducted immediately and the entitlement written to your account.',
    showcaseRequired: 'This listing has no cover or storefront sample and cannot be purchased',
    deliverable: {
      licensedWork: 'You receive playback and license rights. After an exclusive sale, only you and the author can play it.',
      asset: 'You receive download or studio asset-library import rights.',
      script: 'You can import the script snapshot into Writer or Production.',
      creative: 'You can import the canvas snapshot and storefront clips into Creative.',
      generic: 'You receive the entitlement attached to this listing.',
    },
    soldOut: 'Exclusive listing sold',
    soldOutHint: 'This exclusive license has been sold and is no longer available.',
    purchaseOk: 'Purchase complete — entitlement granted',
    notFound: 'Listing not found or unavailable',
    createTitle: 'Submit for platform review',
    selectWork: 'Published work with active copyright',
    fieldTitle: 'Listing title',
    fieldSummary: 'Summary (optional)',
    submitPlatform: 'Submit to platform',
    selectAssets: 'Select assets (multi)',
    assetsSelected: '{{count}} selected',
    assetsEmpty: 'No assets yet — upload one first',
    uploadAsset: 'Upload asset',
    assetsRequired: 'Pick at least one asset for a bundle',
    workRequired: 'Select a published work with active copyright',
    purchasesEmpty: 'No purchases yet',
    listingsEmpty: 'No listings yet',
    myPurchasesSubtitle: 'View point purchases and license status',
    myListingsSubtitle: 'Create and manage marketplace listings',
    orderStatus: 'Status',
    detail: {
      price: 'Price',
      license: 'License',
      summary: 'Listing summary',
      soldTimes: 'Times sold',
      samples: 'Storefront samples',
      deliverable: 'What you get',
    },
    newListing: 'New listing',
    saveDraft: 'Save draft',
    submitReview: 'Submit for review',
    editListing: 'Edit listing',
    editListingHint: 'Only draft or rejected listings can be edited',
    createListingHint: 'Saved as draft — submit for platform review when ready',
    fieldTitleRequired: 'Please enter a listing title',
    deleteListingTitle: 'Delete listing',
    deleteListingConfirm: 'Delete "{{title}}"? This cannot be undone.',
    offlineListingTitle: 'Unpublish listing',
    offlineListingConfirm: 'Unpublish "{{title}}"? Buyers will no longer see it in the catalog. You can submit for review again later.',
    offlineListingSuccess: 'Listing unpublished',
    listingStatus: {
      draft: 'Draft',
      pending_platform: 'Pending review',
      published: 'Published',
      sold: 'Sold',
      offline: 'Offline',
      rejected: 'Rejected',
    },
    orderStatusLabel: {
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    entitlement: {
      sectionTitle: 'Studio entitlements',
      sectionHint: 'After buying a studio script or creative canvas listing, import it into Creation Studio.',
      ordersTitle: 'Purchase orders',
      empty: 'No studio entitlements',
      emptyHint: 'Purchases of studio_script / studio_creative listings appear here.',
      importWriter: 'Import to Writer',
      importCreative: 'Import to Creative',
      importProductionHint: 'Import to Production (Studio)',
      openStudio: 'Open Creation Studio',
      importOk: 'Imported into Creation Studio',
      alreadyImported: 'Already imported',
      importForbidden: 'Import failed — check studio URL and workspace quota',
      statusPending: 'Pending import',
      statusImported: 'Imported to {{target}}',
    },
  },
  userMenu: {
    title: 'Account',
    pointsUnit: 'pts',
    dashboard: 'Creator overview',
    publicProfile: 'Public profile',
    security: 'Account & security',
    profile: 'Profile',
    mySpace: 'My space',
    feedback: 'Feedback',
    myWorks: 'My works',
    revenue: 'Revenue',
    recharge: 'Recharge points',
    withdrawals: 'Withdrawals',
    copyright: 'Copyright',
    subscription: 'Membership',
    storage: 'Object storage',
    groups: {
      center: 'Account',
      manage: 'Manage',
      services: 'Services',
    },
  },
  auth: {
    login: 'Sign in',
    logout: 'Sign out',
    register: 'Sign up',
    getStarted: 'Get started',
    enter: 'Sign in / Sign up',
    currentUser: 'Current user',
    pointsBalance: 'Points balance',
    close: 'Close',
    backHome: 'Back to home',
    market: {
      cn: 'Mainland China',
      global: 'International',
    },
    loginRequiredHint: 'Sign in to unlock full features. New accounts receive welcome points.',
    loginPage: {
      title: 'Sign in',
      tagline: 'Sign in to SilkReels and start creating',
      taglineTenant: 'Sign in to {{name}} and start creating',
      identifier: 'Username / email / phone',
      identifierCn: 'Username / phone',
      identifierGlobal: 'Username / email',
      password: 'Password',
      passwordRequired: 'Enter your password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      failed: 'Sign in failed',
      invalidCredential: 'Incorrect account or password',
      locked: 'Too many failed attempts. The account is temporarily locked',
      accountInactive: 'This account is inactive',
      networkError: 'Network error, please retry',
      noAccount: "Don't have an account?",
      goRegister: 'Sign up',
    },
    registerPage: {
      title: 'Sign up for SilkReels',
      tagline: 'Get 500 free points and start creating',
      taglineCn: 'Register with phone verification — 500 points for new users',
      taglineGlobal: 'Register with email — 500 points for new users',
      username: 'Username',
      usernameHint: '3-64 chars, letters and digits only',
      usernameRequired: 'Username is required',
      email: 'Email',
      emailRequired: 'Email is required',
      emailInvalid: 'Enter a valid email address',
      passwordRequired: 'Password must be at least 6 characters',
      emailOptional: 'Email (optional)',
      phone: 'Phone number',
      phonePlaceholder: 'Mobile number',
      phoneOptional: 'Phone (optional; SMS verification required if provided)',
      nicknameOptional: 'Nickname (optional)',
      password: 'Password',
      passwordHint: '8-72 chars, letters and digits',
      confirmPassword: 'Confirm password',
      confirmPasswordHint: 'Re-enter your password',
      passwordMismatch: 'Passwords do not match',
      passwordPolicy: 'Password must be at least 8 characters and include letters and digits',
      submit: 'Create account',
      submitEnter: 'Create account & continue',
      submitting: 'Creating…',
      failed: 'Sign up failed',
      usernameTaken: 'This username is already taken',
      emailTaken: 'This email is already registered',
      phoneTaken: 'This phone number is already registered',
      tooFrequent: 'Too many sign-up attempts. Please try again later',
      seatLimit: 'This tenant has no available seats',
      networkError: 'Network error, please retry',
      haveAccount: 'Already have an account?',
      goLogin: 'Back to sign in',
    },
    referralCode: {
      label: 'Referral code (optional)',
      placeholder: 'Enter agent referral code if you have one',
      invalid: 'This referral code is invalid or unavailable',
    },
    tenantInvite: {
      label: 'Registration invite code',
      placeholder: 'Enter the invite code from your administrator',
      required: 'Enter the registration invite code first',
      invalid: 'Incorrect registration invite code',
    },
    passwordToggle: {
      show: 'Show password',
      hide: 'Hide password',
    },
    methods: {
      phone: 'Phone',
      password: 'Email & password',
      sso: 'Enterprise SSO',
    },
    optionalFields: {
      toggle: 'More options (referral code, etc.)',
    },
    forgotPassword: {
      link: 'Forgot password?',
      tagline: 'Reset your password with SMS verification',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
      submit: 'Reset password',
      submitting: 'Resetting…',
      failed: 'Password reset failed',
      passwordMismatch: 'Passwords do not match',
      successTitle: 'Password updated',
      successHint: 'Sign in with your new password',
      unavailable: 'SMS verification is not enabled on this site',
    },
    tenant: {
      multiHint: 'Multi-tenant · {{name}} ({{code}}) — accounts and plans are isolated per tenant',
    },
    sms: {
      code: 'SMS code',
      send: 'Send code',
      sending: 'Sending…',
      resendIn: 'Resend in {{sec}}s',
      sendFailed: 'Failed to send verification code',
      providerFailed:
        'SMS provider rejected the request. Check sign name and template in tenant cloud integrations.',
      templateMismatch:
        'SMS template mismatch: use a verification template with variable ${code} and set the matching template code in cloud integrations.',
      phoneRequired: 'Enter your phone number first',
      phoneInvalid: 'Enter a valid phone number',
      phoneNotRegistered: 'This phone number is not registered',
      codeRequired: 'Enter the 6-digit SMS code',
      tooFrequent: 'Too many requests, please try again later',
      dailyLimit: 'Daily SMS limit reached',
    },
    email: {
      code: 'Email code',
      send: 'Send code',
      sending: 'Sending…',
      resendIn: 'Resend in {{sec}}s',
      sendFailed: 'Failed to send verification email. Check platform SMTP settings.',
      checking: 'Checking email…',
      registeredHint: 'This email is registered — please sign in',
      newHint: 'New email — verify to create an account',
      codeRequired: 'Enter the 6-digit email code',
      invalid: 'Invalid or expired email code',
      expired: 'Email code expired — request a new one',
      tooFrequent: 'Too many requests, please try again later',
      dailyLimit: 'Daily email code limit reached',
    },
    phoneOtp: {
      title: 'Phone sign-in / sign-up',
      tagline: 'Verify to sign in; new numbers are registered automatically',
      checking: 'Checking phone number…',
      registeredHint: 'This number is registered — verify to sign in',
      newHint: 'New number — verify to create an account and sign in',
      submit: 'Verify & sign in',
      submitRegister: 'Verify & sign up',
      submitEnter: 'Verify & continue',
      submitting: 'Verifying…',
      failed: 'Verification failed, check your code',
      codeInvalid: 'Invalid SMS code',
      codeExpired: 'SMS code expired — request a new one',
      unavailable: 'SMS sign-in is not enabled on this site',
    },
    captcha: {
      required: 'Please complete the captcha first',
      failed: 'Captcha verification failed, please try again',
    },
    adaptive: {
      signInTagline: 'Enter your phone or email to continue',
      identifier: 'Phone / email / username',
      identifierPlaceholder: 'Phone / email / username',
      identifierPlaceholderPhoneFirst: 'Phone / email',
      identifierRequired: 'Enter phone, email, or username',
      usePhoneInstead: 'Sign in with phone',
      useEmailInstead: 'Sign in with email or username',
      ssoDivider: 'Or use enterprise SSO',
      ssoOidc: 'Sign in with OIDC',
      ssoSaml: 'Sign in with SAML',
      ssoSamlHint: 'SAML users: sign in via your corporate IdP portal',
    },
  },
  site: {
    footer: {
      explore: 'Site navigation',
      copyright: '© {{year}} {{name}}. All rights reserved.',
    },
  },
  account: {
    publicProfile: {
      title: 'Public profile',
      subtitle: 'Set your avatar, display name, and bio — visible on your creator space',
      previewSpace: 'Preview my space',
      avatar: 'Avatar',
      avatarHint: 'JPG / PNG / WebP, max 512KB',
      avatarUploaded: 'Avatar uploaded',
      avatarUploadFailed: 'Avatar upload failed',
      avatarTypeError: 'Please choose an image file',
      avatarSizeError: 'Image must be 512KB or smaller',
      removeAvatar: 'Remove avatar',
      coverTitle: 'Cover / life photo',
      coverHint: 'Wide banner (21:9 recommended, max 2MB) shown at the top of your creator space',
      coverEmpty: 'No cover image yet',
      coverUpload: 'Upload cover',
      coverUploaded: 'Cover uploaded',
      coverUploadFailed: 'Cover upload failed',
      coverSizeError: 'Cover must be under 2MB',
      removeCover: 'Remove cover',
      socialTitle: 'Social links',
      socialHint: 'Bilibili, YouTube, personal site, etc. Up to 6 links',
      socialAdd: 'Add link',
      socialEmpty: 'No links yet',
      socialWebsite: 'Website',
      socialLabelPlaceholder: 'Label',
      socialMax: 'Up to 6 links',
      featuredTitle: 'Featured works',
      featuredHint: 'Pin up to 6 published works — shown at the top of your creator space',
      featuredEmpty: 'No published works yet — publish first, then pin featured works here',
      featuredMax: 'You can pin at most 6 featured works',
      nickname: 'Display name',
      nicknamePlaceholder: '2–64 characters; leave blank to use username',
      usernameReadonly: 'Username cannot be changed',
      bio: 'Bio',
      bioPlaceholder: 'Tell others about you and your creative focus…',
      save: 'Save profile',
      saving: 'Saving…',
      saveSuccess: 'Public profile updated',
      saveFailed: 'Failed to save',
    },
    security: {
      title: 'Account & security',
      subtitle: 'Verify your identity, and bind or change your email and phone',
      username: 'Username',
      email: 'Email',
      noEmail: 'Not bound',
      currentPhone: 'Phone',
      noPhone: 'Not bound',
      bindEmailTitle: 'Bind / change email',
      newEmail: 'New email',
      emailPlaceholder: 'name@example.com',
      bindEmailSubmit: 'Confirm bind',
      bindingEmail: 'Binding…',
      bindEmailSuccess: 'Email updated',
      bindEmailFailed: 'Failed to bind email',
      bindTitle: 'Bind / change phone',
      newPhone: 'New phone number',
      phonePlaceholder: 'Mobile number',
      bindSubmit: 'Confirm bind',
      binding: 'Binding…',
      bindSuccess: 'Phone number updated',
      bindFailed: 'Failed to bind phone',
      realnameTitle: 'Identity verification',
      realnameHint: 'Withdrawals may require a verified identity. This site currently uses manual review: submit your legal name and the last four digits of your ID only. China’s national digital identity (网号/网证) requires institutional onboarding and is not enabled here.',
      realnameStatus: 'Status',
      realnameStatus_unverified: 'Not verified',
      realnameStatus_pending: 'Under review',
      realnameStatus_verified: 'Verified',
      realnameStatus_rejected: 'Rejected — you can resubmit',
      realnameName: 'Legal name',
      realnameNamePlaceholder: 'Name as on your ID',
      realnameNameRequired: 'Enter your legal name',
      realnameLast4: 'ID last 4 digits',
      realnameLast4Placeholder: 'e.g. 123X',
      realnameLast4Required: 'Enter the last four digits of your ID',
      realnameSubmit: 'Submit for review',
      realnameSubmitting: 'Submitting…',
      realnameSubmitSuccess: 'Submitted. Waiting for admin review.',
      realnameSubmitFailed: 'Failed to submit identity verification',
      realnameNationalNidUnavailable:
        'National network identity is not onboarded. Submit your legal name and ID last 4 digits for manual review.',
      realnamePendingHint: 'Submitted. Please wait for an admin to review it.',
      realnameVerifiedHint: 'Identity verified. Withdrawals and other payouts can proceed.',
    },
    profile: {
      title: 'Account profile',
      subtitle: 'View basic info and bind your phone number',
      username: 'Username',
      email: 'Email',
      currentPhone: 'Phone',
      noPhone: 'Not bound',
      bindTitle: 'Bind / change phone',
      newPhone: 'New phone number',
      phonePlaceholder: 'Mobile number',
      bindSubmit: 'Confirm bind',
      binding: 'Binding…',
      bindSuccess: 'Phone number updated',
      bindFailed: 'Failed to bind phone',
    },
    subscriptionPanel: {
      title: 'Membership & collaboration',
      manage: 'Subscription center',
      empty: 'No active subscription',
      membership: 'Personal membership',
      team: 'Team plan',
      expires: 'Expires {{date}}',
      teamCta: 'Get team plan (includes collaboration seats)',
    },
  },
  common: {
    loading: 'Loading…',
    empty: 'No data',
    refresh: 'Refresh',
    confirm: 'Confirm',
    cancel: 'Cancel',
    submit: 'Submit',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    back: 'Back',
    next: 'Next',
    prev: 'Prev',
    search: 'Search',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    actions: 'Actions',
    detail: 'Details',
    insufficientPoints: 'Insufficient points',
    success: 'Operation succeeded',
    coverImageUpload: {
      replace: 'Replace',
      remove: 'Remove',
      uploaded: 'Uploaded',
      loadError: 'Image failed to load. Please upload again.',
    },
    failed: 'Operation failed',
    needLogin: 'Please sign in to access this page.',
    goLogin: 'Sign in',
    processing: 'Processing…',
    submitting: 'Submitting…',
    close: 'Close',
    retry: 'Retry',
    prevPage: 'Previous',
    nextPage: 'Next',
    loadFailed: 'Failed to load',
    loadFailedHint: 'Check your connection and try again',
    changeLanguage: 'Change language',
    openMenu: 'Open navigation menu',
    siteNav: 'Site navigation',
  },
  catalog: {
    viewDetail: 'View details',
  },
  workStatus: {
    draft: 'Draft',
    pending: 'Pending review',
    published: 'Published',
    offline: 'Offline',
    rejected: 'Rejected',
  },
  dashboard: {
    title: 'Creator overview',
    subtitle: 'Works, tips, and revenue at a glance — manage works published from the studio here',
    completeProfileTitle: 'Complete your public profile',
    completeProfileHint: 'Add avatar, nickname, and bio so others can get to know you on your creator space',
    workflow: {
      studio: 'Studio',
      steps: 'Publish work · Review in My works · Show on Works square',
    },
    nav: {
      overview: 'Overview',
      works: 'My works',
      revenue: 'My revenue',
      profile: 'Profile',
    },
    stats: {
      worksTotal: 'Total works',
      worksSub: 'Published {{published}} · Drafts {{draft}}',
      viewsTotal: 'Total views',
      viewsSub: 'Likes {{likes}}',
      tipsTotal: 'Tips received (points)',
      tipsSub: '{{count}} tips · {{givers}} supporters',
      revenueAvailable: 'Available revenue (points)',
      revenueSub: 'Lifetime {{lifetime}}',
    },
    trend: {
      title: '30-day trend',
      empty30d: 'No tips / revenue in the last 30 days',
      empty30dSub: 'Keep creating and publishing — your stats will show up here',
      tips: 'Tips',
      revenue: 'Revenue',
    },
    recentTips: 'Recent tips received',
    recentTipsEmpty: 'No tips yet',
    recentTipsEmptySub: 'Publish and share your works — tips will appear here',
    recentWorks: 'Recently updated works',
    recentWorksEmpty: 'No works yet',
    recentWorksEmptySub: 'Finish clips in the studio, then publish to My works in one step',
    createFirst: 'Go to studio',
    viewAll: 'View all',
    manageWorks: 'Manage works',
    tipUser: 'User #{{n}}',
    tipMeta: 'Work #{{n}}',
    workMeta: '{{status}} · {{views}} views · ♥ {{likes}}',
  },
  recharge: {
    title: 'Recharge points',
    subtitle: 'Tippable {{tippable}} · Promo {{promo}} (recharge credits the tippable bucket)',
    payMethod: 'Payment method',
    pay: {
      points: 'Pay with points',
      channelAlipay: 'Alipay',
      channelWechat: 'WeChat Pay',
      channelPayPal: 'PayPal',
    },
    priceCny: '¥{{cny}}',
    tippableHint: 'Credits tippable points — tips, withdrawal and AI spend',
    buy: 'Recharge now',
    scanPay: 'Scan to pay',
    waitingPaid: 'Payment page opened — points arrive automatically after payment',
    noChannels: 'No payment channels available. Ask an admin to configure Alipay/WeChat/PayPal, or ensure the backend is running in local dev mode.',
    paidSuccess: 'Recharge complete — points credited',
  },
  revenue: {
    title: 'My Revenue',
    subtitle:
      'Available revenue is tracked per source. Convert to points or apply for withdrawal (M5: convert is live).',
    bySource: 'By source',
    txTitle: 'Revenue records',
    txTitleFiltered: 'Revenue records · {{source}}',
    txEmpty:
      'No revenue records yet. Keep creating + publishing — tips and contest rewards will land here.',
    backToWorks: 'My works',
    clearFilter: 'Clear filter',
    pointsUnit: 'points',
    rowCount: '{{n}} entries',
    overview: {
      availablePoints: 'Available revenue (points)',
      availablePointsHint: 'Includes tips, contest prizes and other point-denominated revenue',
      availableCny: 'Available revenue (CNY)',
      availableCnyHint: 'Includes ad share, subscription share and license buyouts',
      sourceCount: 'Active source count',
      sourceCountHint: '/ 6 total sources',
    },
    withdraw: {
      title: 'Cash out',
      subtitle:
        'Withdraw revenue directly to a bank card / Alipay, or trade points for cash',
      entry: 'Withdraw / history',
    },
    table: {
      time: 'Time',
      source: 'Source',
      points: 'Points',
      amount: 'Amount',
      status: 'Status',
      note: 'Note',
      footnote: 'Showing first {{shown}} of {{total}} entries (pagination TBD)',
    },
    sources: {
      ad_share: 'Ad share',
      tip: 'Tips',
      license_sale: 'License sales',
      contest_prize: 'Contest prizes',
      subscription_share: 'Subscription share',
      platform_buyout: 'Platform buyout',
    },
    status: {
      available: 'Available',
      converted: 'Converted',
      withdrawn: 'Withdrawn',
      frozen: 'Frozen',
    },
    convert: {
      title: 'Convert revenue to points (1:1)',
      summary: '{{count}} selected · {{total}} points total',
      selectAll: 'Select all available ({{n}})',
      clear: 'Clear selection',
      submit: 'Convert to points',
      submitting: 'Converting…',
      success: 'Converted {{total}} points. Balance: {{balance}}',
    },
  },
  subscription: {
    title: 'Membership',
    subtitle: 'Monthly, quarterly, or yearly plans with a fixed monthly AI credit quota.',
    subtitleCollab:
      'Studio collaboration is separate from membership credits: unlock team invites, departments, and collaboration tasks in Creation Studio.',
    collabSeats: '{{n}} collaboration seats',
    subtitleTeam: 'Includes collaboration seats for inviting members to production projects.',
    teamSeatsActive: '{{n}} collaboration seats included',
    teamSeatsHeading: 'Collaboration seats',
    teamSeatsUnit: 'seats',
    teamSeatsHint: 'Applies to the project owner account · invite members to productions',
    teamSubscribeSuccess: '「{{plan}}」team plan is active — return to Creation Studio to invite members',
    collabSeatsHeading: 'Collaboration seats',
    collabSeatsUnit: 'seats',
    collabSeatsHint: 'Applies to the project owner account · invite members to productions',
    collabSubscribeSuccess: '「{{plan}}」is active — return to Creation Studio to invite members',
    returnToStudio: 'Return to Creation Studio',
    storeSection: 'Choose a plan',
    audiencePersonal: 'Personal',
    audienceTeam: 'Team',
    audienceTabTeam: 'Team',
    cycleMonthly: 'Monthly',
    cycleQuarterly: 'Quarterly',
    cycleYearly: 'Yearly',
    cycleTabMonthly: 'Monthly',
    cycleTabQuarterly: 'Quarterly',
    cycleTabYearly: 'Yearly',
    cycleQuarterlyHint: 'Save more',
    cycleYearlyHint: 'Best value',
    cycleDiscountZhe: '{{zhe}}×',
    cycleDiscountOff: '{{pct}}% off',
    pricePeriod: {
      monthly: '/mo',
      quarterly: '/qtr',
      yearly: '/yr',
    },
    pointsPeriod: {
      monthly: 'mo',
      quarterly: 'qtr',
      yearly: 'yr',
    },
    monthlyReset: '{{n}} credits / month · resets monthly',
    monthlyResetDuringPeriod:
      '{{n}} credits reset every month for {{months}} months · unused credits do not roll over',
    monthlyGrantHeading: 'Monthly member credits',
    pointsUnit: 'credits',
    periodTotalHint: 'Total in this billing period',
    pointUnitYuan: '1 credit = ¥{{price}}',
    emptyPlans: 'No plans available for this category and billing cycle',
    status: {
      active: 'Active',
      expired: 'Expired',
      canceled: 'Canceled',
    },
    memberQuota: 'Member credits this month: {{n}} / {{monthly}}',
    memberQuotaShort: 'Credits this month: {{n}} / {{monthly}}',
    perkMonthlyPoints: '{{n}} credits / month (resets monthly)',
    perkMonthlyPointsPeriod: '{{n}} credits / month for {{months}} months',
    perkTeamSeats: '{{n}} collaboration seats',
    perDaysShort: '{{n}} days',
    pointsBreakdown: 'General {{general}} + Member {{bonus}}',
    teamSeats: '{{n}} seats',
    faqTitle: 'FAQ',
    moreQuestions: 'More questions',
    contactPrefix: 'Contact: ',
    current: 'Current plan',
    expiresAndStatus: 'Expires {{date}} · status {{status}} · {{auto}}',
    expiresAt: 'Expires {{date}}',
    autoRenewOn: 'Auto-renew enabled',
    autoRenewOff: 'Auto-renew off',
    cancelAuto: 'Turn off auto-renew',
    canceling: 'Processing…',
    perDays: 'points / {{n}} days',
    monthly: '{{n}} credits / month',
    monthlyBonus: '{{n}} credits / month',
    subscribed: 'Subscribed',
    subscribe: 'Subscribe',
    recommended: 'Recommended',
    bottomHint:
      'Want to top up first? Convert revenue at <1>My Revenue</1> (1:1) and apply it immediately.',
    revenueLink: 'My Revenue',
    alreadyHas: 'You already have an active subscription. Wait until it expires or cancel auto-renew first.',
    pointsInsufficient: 'Insufficient points. Please check in or top up first.',
    subscribeSuccess: 'Membership activated: {{plan}}',
    cancelSuccess: 'Auto-renew disabled (current subscription stays valid until expiry).',
    hasActivePlan: 'You already have an active plan. Switch after it expires.',
    blocked: 'Unavailable',
    upgrade: 'Upgrade plan',
    cannotDowngrade: 'Cannot downgrade',
    cannotSwitchCycle: 'Switch billing cycle or plan type after current period ends',
    upgradeSuccess: 'Upgraded to "{{plan}}" — expiry date unchanged.',
    pay: {
      title: 'Subscribe to {{plan}}',
      upgradeTitle: 'Upgrade to {{plan}}',
      upgradeHint: 'Upgrade from "{{from}}" — pay the difference only; expiry stays the same.',
      amount: 'Amount due',
      upgradeAmount: 'Upgrade difference',
      upgradeNote: 'Your current expiry date is kept; the billing period does not reset.',
      pointsAmount: '{{n}} points',
      cashAmount: '¥ {{n}}',
      balance: 'Available: {{n}} credits (member quota spent first)',
      balanceDetail: 'General {{general}} · Member {{bonus}}',
      includesMonthly: 'Includes {{n}} member credits, reset every month',
      method: 'Payment method',
      points: 'Pay with points',
      channelAlipay: 'Alipay',
      channelWechat: 'WeChat Pay',
      channelPayPal: 'PayPal',
      confirm: 'Confirm payment',
      upgradeConfirm: 'Confirm upgrade',
      channelUnavailable: 'This payment channel is not configured. Set up Alipay/WeChat/PayPal in admin settings, or pay with points.',
      noChannels: 'No payment channels available. Use points or ask an admin to configure payments.',
      realHint: 'Complete payment in the checkout window. Your subscription activates automatically after payment.',
      openPayWindow: 'Open checkout',
      waitingPaid: 'Waiting for payment confirmation…',
    },
  },
  storage: {
    title: 'Object storage',
    subtitle: 'Manage tenant cloud quota and your own OSS/COS profile for productions.',
    usage: 'Used',
    freeIncluded: 'Includes {{n}} free',
    addonIncluded: '{{n}} add-on',
    available: '{{n}} available',
    activeAddons: 'Active add-ons',
    expires: 'Expires {{date}}',
    tabs: {
      aria: 'Object storage sections',
      aliyun: 'Aliyun',
      tencent: 'Tencent Cloud',
      plans: 'Add-on plans',
      activeHint: 'Active',
    },
    custom: {
      title: 'Custom object storage',
      hint: 'One Aliyun OSS / Tencent COS profile per user. When you create a production with “Custom object storage”, this profile is snapshotted onto the project (does not use tenant quota).',
      providerTitle: '{{provider}} object storage',
      providerHint: 'We probe bucket access before saving. Secrets are never shown again. Only one profile is kept — saving here replaces the other cloud vendor’s config.',
      notConfigured: 'This cloud vendor is not configured yet. Fill in the form and save to use it for new productions.',
      otherProviderActive: '“{{provider}}” is currently active. Saving here switches to this cloud vendor.',
      configuredSummary: 'Configured: {{provider}} · Bucket {{bucket}} · Region {{region}}',
      save: 'Save',
      clear: 'Clear',
      saveSuccess: 'Custom object storage saved',
      clearSuccess: 'Custom object storage cleared',
      loadFailed: 'Failed to load custom object storage',
      saveFailed: 'Failed to save custom object storage',
      clearFailed: 'Failed to clear custom object storage',
      notConfiguredError: 'Configure custom object storage in Account first',
    },
    plansTitle: 'Expansion plans',
    plansHint: 'Capacity increases immediately after purchase. Add-ons expire when the term ends; files remain, but new uploads need free space or renewal.',
    billingCycle: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
    },
    pricePeriod: {
      monthly: '/mo',
      quarterly: '/qtr',
      yearly: '/yr',
    },
    loginHint: 'Sign in to view storage usage and expansion plans',
    noPlansForCycle: 'No plans for this billing cycle',
    days: ' days',
    pricePoints: '{{n}} pts{{period}}',
    priceCash: '¥{{n}}{{period}}',
    buy: 'Buy now',
    purchaseSuccess: '"{{plan}}" activated — storage expanded',
    footerHint: 'You can also subscribe for monthly points to pay for add-ons:',
    subscriptionLink: 'Membership',
    pay: {
      title: 'Buy {{plan}}',
      amount: 'Amount due',
      pointsAmount: '{{n}} points',
      cashAmount: '¥ {{n}}',
      balance: 'Balance: {{n}} points',
      method: 'Payment method',
      points: 'Pay with points',
      channelAlipay: 'Alipay',
      channelWechat: 'WeChat Pay',
      channelPayPal: 'PayPal',
      confirm: 'Confirm payment',
      channelUnavailable: 'This payment channel is not configured. Set up Alipay/WeChat/PayPal in admin settings, or pay with points.',
      noChannels: 'No payment channels available. Use points or ask an admin to configure payments.',
      realHint: 'Complete payment in the checkout window or scan the QR code. Storage expands automatically after payment.',
      openPayWindow: 'Open checkout',
      waitingPaid: 'Waiting for payment confirmation…',
      pointsInsufficient: 'Insufficient points. Check in or top up first.',
    },
  },
  tipDialog: {
    title: 'Tip "{{title}}"',
    balance: 'Tippable {{n}} · Promo {{promo}} · Total {{total}} pts',
    pickAmount: 'Pick amount',
    customPlaceholder: 'Or enter a custom amount',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Leave the author a few kind words…',
    breakdown:
      'Charge {{amount}} pts · Platform fee {{fee}} · Author receives {{earn}}',
    insufficient:
      'Insufficient tippable points. Promo points can only be used for AI and similar; top up or convert revenue to tip.',
    confirm: 'Confirm tip',
    submitting: 'Submitting…',
    minAmount: 'Please enter an amount greater than 0',
  },
  workDetail: {
    backToGallery: 'Works gallery',
    notFound: 'This work does not exist or is not visible to you.',
    noEpisode: 'No episodes yet',
    noVideo: 'No video for this episode',
    publishedAt: 'Published {{date}}',
    author: 'Author #{{n}}',
    fingerprint: 'Copyright fingerprint · {{hash}}',
    support: 'Support the author',
    tipCount: '{{n}} tips received',
    tipSelf: "You can't tip yourself",
    tipCta: 'Tip with points',
    tipLoginRequired: 'Sign in to tip',
    tipHint:
      'Tips are charged from your points wallet. Platform takes 10%, the rest goes to the author.',
    episodes: 'Episodes ({{n}})',
    noEpisodes: 'No episodes yet',
    moreInfo: 'Work notes',
    stat: {
      episodes: 'Episodes',
      published: 'Published',
    },
    process: {
      title: 'Production process',
      canvas: 'Canvas preview',
      assets: 'Assets',
      source: 'Source canvas',
      empty: 'The author has not shared a production process (only works published from Creative Studio).',
      noAssets: 'No previewable assets',
      copy: 'Copy to my creative canvas',
      copyOk: 'Copied to your creative workspace',
      loginHint: 'Sign in to copy this project into your creative canvas.',
      readOnlyHint: 'Read-only canvas preview — pan and zoom to explore nodes; editing is disabled.',
    },
  },
  home: {
    titleSuffix: 'AI Comic Drama Workbench',
    intro:
      'Node-based multimodal generation + international contests + platform copyright registry.',
    welcome: 'Welcome back, <1>{{user}}</1>.',
    guestHint:
      'Guests can browse works and contests. Workbench, copyright and revenue require sign-in.',
    enterProjects: 'Open projects',
    browseWorks: 'Browse {{title}}',
    navTitle: 'Quick links',
    hero: {
      badge: 'International · Open Contest',
      title: 'Join the Global AI Creative Revolution',
      subtitle:
        'Connect with creators worldwide, turn text into cinematic comics, compete internationally, and showcase your work on the world stage.',
      ctaCreate: 'Start Creating',
      ctaExplore: 'Explore Global Trending Works',
    },
    announcement: {
      remaining: '{{days}}d {{hours}}h {{minutes}}m {{seconds}}s left',
      label: 'Ends in',
      days: 'd',
      hours: 'h',
      minutes: 'm',
      seconds: 's',
    },
    carousel: {
      prev: 'Previous slide',
      next: 'Next slide',
      play: 'Play',
      pause: 'Pause',
      goTo: 'Slide {{n}}',
      seasonEnded: 'Season ended',
      countdownDays: '{{days}}d {{hours}}h left',
      countdownHours: '{{hours}}h {{mins}}m left',
      topRanking: 'TOP Ranking',
      votesUnit: 'votes',
      contestFallback:
        'Always-on international AI comic contest with hybrid judging — creators worldwide compete together.',
      workFallback: 'Featured AI comic from our global creator community',
      badges: {
        contest: 'Global Contest',
        featured: 'Global Featured',
      },
      fallback: {
        slide1: {
          title: 'Join the Global AI Creative Revolution',
          subtitle:
            'Connect with creators worldwide, turn text into cinematic comics, compete internationally, and showcase your work on the world stage.',
        },
        slide2: {
          title: 'Neon Nights · Global Creative Contest',
          subtitle:
            'Always-on international AI comic contests with hybrid judging; prize pools shown in USD equivalent — enter anytime.',
        },
        slide3: {
          title: 'From Script to Final Cut, All in One',
          subtitle:
            'Multilingual scripts, character consistency, scene generation and auto composition — take your work global.',
        },
      },
    },
    stats: {
      countries: 'Countries & regions',
      countriesValue: '48+',
      creators: 'Global creators',
      creatorsValue: '50,000+',
      contests: 'Active contests',
      works: 'Public works',
    },
    contests: {
      title: 'Global Creative Contests',
      subtitle:
        'Always-on international AI comic contests with hybrid judging; prizes shown in USD equivalent',
      enter: 'Enter Contest',
      prizeUsd: '≈ {{amount}} USD',
      prizePoints: '{{n}} pts pool',
      viewAll: 'View all contests',
      empty: 'No active contests yet — stay tuned',
    },
    features: {
      title: 'AI Capabilities',
      subtitle: 'Script · character · scene · compose — end-to-end AI comic pipeline',
      viewAll: 'View all capabilities',
      backHome: 'Back to home',
      script: {
        title: 'Script Generation',
        desc: 'AI-assisted storyboards and dialogue; multilingual scripts in one click',
      },
      character: {
        title: 'Character Consistency',
        desc: 'Keep characters consistent across shots; global art styles supported',
      },
      scene: {
        title: 'Scene Generation',
        desc: 'Cyberpunk, fantasy, photoreal — pick visual styles from around the world',
      },
      compose: {
        title: 'Auto Composition',
        desc: 'Sync audio & video; export contest-ready and publish-ready formats',
      },
      capability: {
        title: 'AI Capability',
        desc: 'Composable multimodal AI for comic production — extend as you grow',
      },
      mock: {
        newScript: 'New script',
        type: 'Type',
        typeVal: 'Sci-fi',
        lang: 'Language',
        langVal: 'Chinese',
        scriptText: 'Scene 1: The fleet enters frame; the young commander stands on the bridge…',
        storyboard: 'Storyboard',
        shot1: 'Wide · Federation fleet enters frame…',
        shot2: 'Medium · On the bridge, the young commander…',
        charLib: 'Library',
        myChars: 'My characters',
        similarity: 'Similarity',
        styleMatch: 'Style match',
        sceneLib: 'Scene library',
        myScenes: 'My scenes',
        scene1: 'Future city',
        scene2: 'Ancient court',
        scene3: 'Cyber space',
        scene4: 'Snow peaks',
        cameraCtrl: 'Camera',
        perspective: 'Blur intensity',
        composing: 'Composing… 68%',
        composeNote1: 'Final render in progress — please wait.',
        composeNote2: 'Syncing data — do not close this page.',
        sub1: 'At the edge, hope remains.',
        sub2: 'Commander, unknown signal ahead.',
        aiBadge: 'AI',
        aiTitle: 'AI capability engine',
        aiPrompt: 'Describe intent — multimodal models co-create comic assets',
        powerTitle: 'Capability boost',
        powerPrompt: 'Batch + low-latency inference for high-throughput creation',
        stepUnderstand: 'Understand',
        stepGenerate: 'Generate',
        stepPolish: 'Polish',
        stepReady: 'Ready',
        stepBoost: 'Boost',
        stepDeliver: 'Deliver',
        chipText: 'Script',
        chipImage: 'Image',
        chipVideo: 'Video',
        chipLocale: 'i18n',
        chipSpeed: 'Low latency',
        chipBatch: 'Batch',
        chipQuality: 'Hi-fi',
        confidence: 'Confidence',
        throughput: 'Throughput',
      },
    },
    gallery: {
      title: 'Global Selected Works',
      subtitle: 'Browse trending AI comics by region and discover cross-cultural creativity',
      viewAll: 'View all',
      regions: {
        global: 'Global',
        zh: 'China',
        en: 'English',
        fr: 'France',
        de: 'Germany',
        es: 'Spanish',
        pt: 'Portuguese',
        it: 'Italy',
        id: 'Indonesia',
        ko: 'Korea',
        ja: 'Japan',
        ar: 'Arabic',
      },
      badges: {
        hot: 'Hot',
        top: 'Top',
        pinned: 'Pinned',
      },
      empty: 'No works in this region yet — check global trending',
      views: '{{n}} views',
      featuredBadge: 'Weekly Pick',
      soundOn: 'Sound on',
      soundOff: 'Sound off',
      fullscreen: 'Fullscreen',
      videoLoading: 'Loading video',
      demoTitle: 'Interstellar Edge: Light of Hope',
      demoDesc: 'At the edge of the cosmos, explorers search for the dawn of civilization.',
      demoCreator: 'NovaVision',
      positiveReviews: '98% positive',
    },
    platform: {
      title: 'Platform Data',
      moreData: 'More data ›',
      works: 'Total works',
      satisfaction: 'Satisfaction',
      countries: 'Countries',
      creators: 'Active creators',
      tags: 'Popular tags',
    },
    entries: {
      myProjects: { title: 'My works', desc: 'Manage drafts, submit for review, and publish.' },
      works: { title: 'Works', desc: 'Browse AI comic shorts published by the community.' },
      contests: { title: 'Contests', desc: 'Join international AI comic contests, win prizes and points.' },
      myWorks: { title: 'My Works', desc: 'Review your submissions, published works and revenue.' },
      copyright: { title: 'Copyright', desc: 'Register copyright certificates for your published works.' },
    },
  },
  creator: {
    title: 'Creator Profile',
    notFound: 'Creator not found',
    profile: {
      worksCount: '{{n}} works',
      totalViews: '{{n}} views',
      totalLikes: '{{n}} likes',
      contestAwards: '{{n}} contest awards',
      copyrightCount: '{{n}} copyrights',
      listingCount: '{{n}} listings',
      memberSince: 'Member since {{date}}',
      followers: '{{n}} followers',
      following: '{{n}} following',
      aboutTitle: 'About creator',
      bioTitle: 'Bio',
      noBio: 'This creator has not added a bio yet',
      socialTitle: 'Social links',
    },
    follow: 'Follow',
    unfollow: 'Following',
    share: 'Share',
    shareCopied: 'Link copied',
    badges: {
      member: 'Member',
      platformVerified: 'Verified creator',
      realnameVerified: 'ID verified',
    },
    report: 'Report',
    reportTitle: 'Report this creator',
    reportHint: 'We will review your report. Please describe the issue if you can.',
    reportReasonLabel: 'Reason',
    reportReason: {
      spam: 'Spam / advertising',
      abuse: 'Harassment / inappropriate content',
      impersonation: 'Impersonation',
      other: 'Other',
    },
    reportDetailPlaceholder: 'Additional details (optional)',
    reportSubmit: 'Submit report',
    reportSuccess: 'Report submitted. Thank you.',
    featuredWorks: 'Featured works',
    contestRank: 'Rank #{{n}}',
    sort: {
      label: 'Sort works',
      publishedAt: 'Latest published',
      views: 'Most views',
      likes: 'Most likes',
      createdAt: 'Recently created',
    },
    tabs: {
      works: 'Works',
      contests: 'Contest awards',
      copyrights: 'Copyrights',
      listings: 'Marketplace',
      about: 'About',
    },
    empty: 'This creator has no public works yet',
    emptyContests: 'No contest awards yet',
    emptyCopyrights: 'No public copyright records',
    emptyListings: 'No published listings',
    visitWork: 'Open work',
    workPreview: {
      title: 'Work preview',
      loading: 'Loading work…',
      noVideo: 'No video yet — view the cover or open the full player',
      openFull: 'Full player',
    },
  },
  works: {
    title: 'Works',
    subtitle: 'AI comic shorts published by the community',
    filterAll: 'All',
    creator: 'Creator',
    views: 'Views',
    likes: 'Likes',
    tipBtn: 'Tip',
    viewDetail: 'View',
    noSummary: '(No summary)',
    empty: 'No works yet',
    viewProcess: 'View production process',
  },
  accountWorks: {
    title: 'My works',
    subtitle: 'Review, publish, and unpublish your works.',
    filter: {
      all: 'All',
      status: 'Status',
    },
    empty: 'No works yet',
    emptySub: 'Finish clips in the studio, then use Publish to works — manage review and listing here.',
    goStudio: 'Go to studio',
    row: {
      noSummary: '(No summary)',
      meta: 'Created {{date}} · {{views}} views',
      preview: 'Preview',
      edit: 'Edit',
      submitReview: 'Submit for review',
      publish: 'Publish',
      offline: 'Unpublish',
      delete: 'Delete',
      deleteConfirm: 'Delete "{{title}}"?',
    },
    edit: {
      back: 'My works',
      title: 'Edit work',
      notFound: 'Work not found',
      basic: {
        title: 'Basic info',
        labels: {
          title: 'Title',
          summary: 'Summary',
          description: 'Description (Markdown)',
          coverUrl: 'Cover',
          tags: 'Tags (comma-separated)',
          tagsPh: 'cyberpunk, sci-fi',
          visibility: 'Visibility',
        },
        visibility: {
          public: 'Public',
          unlisted: 'Unlisted',
          private: 'Private',
        },
        save: 'Save',
        saving: 'Saving…',
        savedAt: 'Saved · {{time}}',
      },
      episodes: {
        title: 'Episodes ({{n}})',
        ep: 'Episode title',
        videoUrl: 'Video URL (optional)',
        duration: 'Duration (s)',
        add: 'Add',
        empty: 'At least 1 episode is required to publish.',
        meta: '{{n}}s · {{video}}',
        noVideo: 'No video',
        publish: 'Publish',
        offline: 'Unpublish',
        delete: 'Delete',
        deleteConfirm: 'Delete this episode?',
      },
    },
  },
  withdrawals: {
    title: 'Withdrawals',
    subtitle:
      'Turn available revenue into cash — directly from revenue, or convert points first (1 point = ¥0.01, min 100 pts).',
    backToRevenue: 'My revenue',
    applyPoints: 'Withdraw from points',
    applyCash: 'Withdraw cash',
    empty: 'No withdrawal records yet',
    cancelConfirm: 'Cancel this withdrawal? Locked amount / points will be refunded.',
    table: {
      time: 'Submitted at',
      method: 'Method',
      amount: 'Amount',
      status: 'Status',
      note: 'Note',
      action: 'Action',
    },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      paid: 'Paid out',
      rejected: 'Rejected',
      canceled: 'Canceled',
    },
    methodCashEntries: 'Cash · {{n}} entries',
    methodPoints: 'Points → cash',
    deductPoints: '- {{n}} pts',
    cancel: 'Cancel',
    note: {
      pending: 'Awaiting admin review',
      approved: 'Approved, waiting for payout',
      canceled: 'Canceled',
      paidNote: 'Tx {{note}}',
    },
    cash: {
      title: 'Cash withdrawal',
      subtitle:
        'Only "available + has cash amount" revenue records can be picked; total ≥ ¥100 to submit.',
      loadingRecords: 'Loading records…',
      noRecord: 'No withdrawable revenue. Tips / convert / contest prizes will show up here.',
      selectedSummary: '{{count}} selected · total ¥{{total}}',
      belowMin: '(under ¥100 minimum)',
      bankPh: 'Bank (e.g. CMB Beijing)',
      namePh: 'Receiver name',
      accountPh: 'Account no. (bank card / Alipay)',
      errMin: 'Minimum withdrawal is ¥100, please select more',
      errSelect: 'Please select at least one revenue record',
      errAccount: 'Please fill receiver info completely',
      submit: 'Submit',
    },
    points: {
      title: 'Points withdrawal',
      subtitle: '1 pt = ¥0.01. Minimum 100 pts. Tippable balance {{balance}} (promo points cannot be withdrawn)',
      pointsLabel: 'Points to withdraw',
      gain: 'You will receive ¥{{cny}}',
      namePh: 'Receiver name (must match Alipay real-name)',
      alipayPh: 'Alipay account (phone / email)',
      errMin: 'Minimum 100 pts',
      errInsufficient: 'Insufficient tippable points (promo points cannot be withdrawn)',
      errAccount: 'Please fill receiver name + Alipay account',
      submit: 'Submit',
    },
  },
  copyright: {
    title: 'Copyright Center',
    subtitle:
      'Register on-platform copyright for published works · SHA-256 fingerprint + UTC timestamp + downloadable PDF.',
    needLogin: 'Please sign in before using copyright registration.',
    register: {
      title: 'Apply for registration',
      feeHint:
        'Each registration costs 200 points. Published works only. The system generates a certificate number and a downloadable PDF.',
      pickWork: 'Pick a work (only published + not yet registered)',
      pickWorkPlaceholder: '— Please choose —',
      loadingWorks: 'Loading works…',
      noEligible: 'No eligible published works to register.',
      goPublish: 'Publish one',
      authorName: 'Author byline (optional, defaults to your nickname)',
      authorPlaceholder: 'Leave blank to use nickname',
      submit: 'Submit registration (-200 pts)',
    },
    list: {
      title: 'My registrations',
      count: '{{n}}',
      empty: 'No registrations yet.',
      revoked: 'Revoked',
      certNo: 'Certificate · {{no}}',
      fp: 'fp · {{hash}}',
      authorAndDate: 'Author {{author}} · registered {{date}}',
      downloadPdf: 'Download PDF',
      downloading: 'Downloading…',
      verifyLink: 'Verify link',
    },
    verify: {
      back: 'My copyrights',
      title: 'Certificate verification',
      verifying: 'Verifying…',
      valid: 'Valid',
      revoked: 'Revoked',
      certNo: 'Certificate {{no}}',
      kv: {
        author: 'Author',
        category: 'Category',
        registeredAt: 'Registered at (UTC)',
        fee: 'Platform fee (points)',
        fingerprint: 'Fingerprint (SHA-256)',
        summary: 'Summary',
      },
      footer:
        'This certificate is issued by SilkReels platform attestation and does not replace official copyright office registration. public_id · {{pid}}',
    },
  },
  learning: {
    title: 'Learning Center',
    subtitle: 'Video tutorials and creator guides · free or unlock with points',
    emptyList: 'No tutorials yet',
    noSummary: '(No summary)',
    loginHintTitle: 'Sign in to unlock paid tutorials',
    loginHintDesc: 'Free tutorials need no account; paid content requires sign-in and points.',
    backToList: 'Back to tutorials',
    episodeCount: '{{count}} episodes',
    pricingFree: 'Free',
    pricingPaid: 'Full series {{points}} pts',
    pricingMixed: 'First {{count}} episodes free',
    episodesTitle: 'Episodes ({{n}})',
    episodeNo: 'Ep. {{no}}',
    play: 'Play',
    unlockEpisode: 'Unlock {{points}} pts',
    unlockAll: 'Unlock all',
    unlockAllTitle: 'Unlock all paid episodes',
    unlockAllDesc: 'Pay {{points}} points once for the rest of the series',
    selectEpisodeHint: 'Select an episode and press Play',
    nowPlaying: 'Now playing',
    notFound: 'Tutorial not found or unavailable',
    stat: {
      episodes: 'Episodes',
      pricing: 'Pricing',
      freeEpisodes: 'Free episodes',
      unlockPoints: 'Unlock points',
    },
  },
  contests: {
    title: 'Contests',
    subtitle: 'International rolling AI comic contests · audience votes + expert review.',
    emptyList: 'No public contests yet.',
    guestHint: 'Sign in to submit entries and vote.',
    guestHintTitle: 'Sign in for full contest features',
    backToList: 'Contest list',
    notFound: 'Contest not found.',
    noDescription: '(No description)',
    noSummary: '(No summary)',
    noCover: 'No cover',
    code: 'Code {{code}}',
    entryFee: 'Entry fee {{n}} pts',
    rewardPool: 'Reward pool {{n}} pts',
    submissionDue: 'Due {{date}}',
    filterAll: 'All statuses',
    points: '{{n}} pts',
    submit: 'Submit entry',
    needLogin: 'Please sign in first',
    notOpenSubmit: 'Submission is not open',
    notVoting: 'Voting is not open',
    tabs: {
      latest: 'Latest',
    },
    status: {
      draft: 'Drafting',
      open: 'Open',
      voting: 'Voting',
      judging: 'Judging',
      closed: 'Closed',
    },
    stat: {
      entryFee: 'Entry fee',
      reward: 'Reward pool',
      entries: 'Entries',
      status: 'Status',
    },
    detail: {
      overview: 'About this contest',
      schedule: 'Schedule',
      scheduleRange: 'From start to close',
      scheduleSubmitDue: 'Submission deadline',
      scheduleVoteDue: 'Voting deadline',
      workAbout: 'About the work',
      authorAbout: 'About the creator',
      noWorkAbout: 'No synopsis yet',
      noAuthorAbout: 'This creator has not added a bio yet',
      noEntrySelected: 'Select an entry to read its synopsis',
      authorFallback: 'Creator #{{id}}',
    },
    entries: {
      title: 'Entries',
      empty: 'No approved entries yet.',
      votes: '{{n}} votes',
      judgeAvg: 'Avg {{n}}',
      view: 'Open work',
      vote: 'Vote',
      voting: 'Voting…',
      review: {
        pending: 'In review',
        approved: 'Approved',
        rejected: 'Rejected',
      },
    },
    entryPreview: {
      title: 'Work preview',
      loading: 'Loading work…',
      noVideo: 'No video yet — view the cover or open the full player',
      openFull: 'Full player',
      openExternal: 'Open link',
      externalHint: 'This entry uses an external link. Open it in a new tab below.',
    },
    submitDialog: {
      title: 'Submit to "{{title}}"',
      feeHint: 'Entry fee {{n}} pts, will be deducted on submit.',
      pickWork: 'Pick one of your published works (recommended)',
      noBind: '— No binding (use external link) —',
      titleLabel: 'Display title (optional, defaults to work title)',
      titlePlaceholder: 'Leave blank to use the work title',
      descLabel: 'Summary (optional)',
      urlLabel: 'External URL (required if work_id is empty)',
      urlPlaceholder: 'https://… or silkreels://work/<public_id>',
      coverLabel: 'Cover (optional)',
      confirm: 'Confirm submission',
      successPending: 'Submission received. It will appear here after admin approval.',
    },
    errors: {
      submitFailed: 'Submission failed. Please try again later.',
      alreadySubmitted: 'You have already entered this contest — one submission per person. Check the entries list for review status.',
      alreadySubmittedApproved: 'Your entry is approved and shown in the list on the left.',
      alreadySubmittedRejected: 'Your entry was not approved. {{reason}}',
      contestClosed: 'This contest is not accepting submissions right now.',
      workNotPublished: 'Pick a published work, or publish it under My Works first.',
      workNotOwned: 'You can only submit works that belong to you.',
      workNotFound: 'The linked work was not found or was removed.',
      insufficientPoints: 'Not enough points to pay the entry fee.',
      votingClosed: 'Voting is not open for this contest.',
      judgingClosed: 'Judging is not open for this contest.',
      entryNotApproved: 'This entry is not approved yet and cannot be voted on.',
      alreadyVoted: 'You have already voted in this contest — one vote per person.',
      voteIpBlocked: 'Too many votes from this network. Please try again later.',
      voteFailed: 'Vote failed. Please try again later.',
    },
    alreadySubmittedHint: 'Submitted — pending review',
    submittedApproved: 'Submitted — approved',
    submittedRejected: 'Submission rejected',
    withdraw: 'Withdraw entry',
    withdrawing: 'Withdrawing…',
    withdrawTitle: 'Confirm withdrawal',
    withdrawConfirm: 'Withdraw from "{{title}}"? Your entry will be removed. If you paid an entry fee, {{n}} pts will be refunded.',
    withdrawSuccess: 'Entry withdrawn. Entry fee refunded if applicable.',
    withdrawFailed: 'Withdrawal failed. Please try again.',
    withdrawNotOpen: 'This contest is no longer in the submission phase — withdrawal is not allowed.',
    seasons: {
      sectionTitle: 'Seasons / leaderboards',
      count: '{{n}} seasons',
      loading: 'Loading seasons…',
      loadError: 'Failed to load seasons: {{msg}}',
      empty: 'No leaderboard season yet — new entries appear in the overall list.',
      pendingSettle: 'Season not settled yet. Ranking and rewards will be published after settle.',
      rankLoading: 'Loading ranking…',
      rankEmpty: 'No reward records for this season.',
      rankRow: 'entry-{{id}}',
      reward: '+{{n}} pts',
      status: {
        open: 'Accepting',
        judging: 'Judging',
        settled: 'Settled',
        archived: 'Archived',
      },
    },
  },
  feedback: {
    pageTitle: 'Feedback center',
    pageSubtitle: 'Report bugs or share suggestions — earn points when approved',
    newFeedback: 'New feedback',
    empty: 'No feedback yet. Tell us how we can improve.',
    totalCount: '{{n}} total',
    listHint: 'Tap an item to view details',
    reviewNote: 'Platform reply',
    submittedAt: 'Submitted',
    reviewedAt: 'Reviewed',
    pageUrl: 'Related page',
    attachments: 'Attachments',
    playVideo: 'Play video',
    rewardGranted: 'Approved — {{points}} points granted',
    dialogTitle: 'Submit feedback',
    dialogSubtitle: 'Describe the issue; earn points when approved',
    rewardBannerTitle: 'Earn points for helpful feedback',
    rewardBannerSelected: 'This category: up to +{{points}} pts if approved',
    rewardBannerMax: 'Bug reports: up to +{{points}} pts (subject to review)',
    rewardHint: 'Up to +{{points}} pts if approved',
    floatLabel: 'Help & feedback',
    floatFeedback: 'Feedback',
    floatSupport: 'Support',
    floatDragHint: 'Drag to move',
    csComingSoon: 'Live chat is coming soon — please use Feedback for now',
    supportDialog: {
      subtitle: 'Reach us through the channels below',
      phone: 'Phone',
      email: 'Email',
      qrcode: 'QR code',
      loading: 'Loading support contacts…',
      empty: 'Support contacts are not configured yet. Please try again later or use Feedback.',
    },
    viewHistory: 'My feedback',
    dashboardLink: 'View my feedback history and review status',
    categoriesShort: {
      bug: 'Bug',
      suggestion: 'Idea',
      experience: 'UX',
      other: 'Other',
    },
    categories: {
      bug: 'Bug',
      suggestion: 'Suggestion',
      experience: 'UX issue',
      other: 'Other',
    },
    status: {
      pending: 'Pending',
      rewarded: 'Rewarded',
      rejected: 'Rejected',
    },
    form: {
      category: 'Category',
      title: 'Title (optional)',
      titlePlaceholder: 'Short summary',
      content: 'Details',
      contentPlaceholder: 'Steps to reproduce, expected behavior, or your idea…',
      contentRequired: 'Please enter feedback details',
      attachments: 'Screenshots / video',
      attachHint: 'Up to 4 images or clips to help us reproduce the issue',
      addMedia: 'Add',
      maxAttachments: 'Maximum {{n}} attachments',
      invalidMedia: 'Images and videos only',
      waitUpload: 'Please wait for uploads to finish',
      submit: 'Submit',
      submitSuccess: 'Thanks — your feedback was submitted!',
    },
  },
  messages: {
    title: 'Messages',
    subtitle: 'Studio updates, task progress, and feedback results',
    bellLabel: 'Notifications',
    empty: 'No messages yet',
    unread: 'Unread',
    unreadCount: '{{count}} unread',
    viewAll: 'View all messages',
    markAllRead: 'Mark all as read',
    markReadFailed: 'Could not mark as read',
    markAllFailed: 'Could not mark all as read',
    loadMore: 'Load more',
    filters: {
      all: 'All',
      unread: 'Unread',
    },
    categories: {
      all: 'All categories',
      studio: 'Studio',
      task: 'Tasks',
      account: 'Account',
      system: 'System',
    },
  },
}
